import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Autocomplete,
	Chip,
	Grid,
	Divider,
	IconButton,
	Paper,
	Skeleton,
	Stack,
	TextField,
	Typography,
	LinearProgress,
	Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import MouseOutlinedIcon from "@mui/icons-material/MouseOutlined";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import PageTitle from "../../components/PageTitle/PageTitle";
import ReactECharts from "echarts-for-react";
import DateRange from "../../components/DateRange/DateRange";
import ChartLoading from "../../components/Carts/ChartLoading/ChartLoading";
import Datagrid from "../../components/Datagrid/Datagrid";
import { getUserAnalyticsDashboard } from "../../services/openpanelAnalyticsService";
import { getAllUsers, getMachinesByProvision } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import { getRoles } from "../../services/createEditUSerFormService";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import { useOptions } from "../../context/optionContext";
import {
	getActiveFilters,
	isAllValue as isAllValueUtil,
} from "../../services/analyticsFilteringService";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
import { canAccessUserAnalytics } from "../../utilities/pageAccess";
import analyticsKpiTooltips from "../../components/Analytics/analyticsKpiTooltips.json";

const DATE_RANGE_BUTTONS = [
	{ key: "T", label: "Today", tooltip: "Today" },
	{ key: "W", label: "Last 7 Days", tooltip: "Week (Last 7 Days)" },
	{ key: "M", label: "Month", tooltip: "Month" },
	{ key: "Q", label: "Quarter", tooltip: "Quarter" },
	{ key: "Y", label: "Year", tooltip: "Year" },
];

const FILTER_KEYS = [
	{ key: "legal_entity", label: "Legal Entity", allLabel: "All Legal Entities" },
	{ key: "customer_id", label: "Bakery", allLabel: "All Bakeries" },
	{ key: "site_id", label: "Bakery Location", allLabel: "All Bakery Locations" },
	{ key: "machine_id", label: "Machine", allLabel: "All Machines" },
	{ key: "user_role", label: "User Role", allLabel: "All User Roles" },
	{ key: "user_email", label: "User", allLabel: "All Users" },
];

const ALL_VALUE = "All";
const ALL_OPTION = { value: ALL_VALUE, label: ALL_VALUE, searchText: "all" };
const OPTION_LIMIT = 500;
const DEFAULT_USER_ANALYTICS_PAGE_SIZE = 10;
const USER_ANALYTICS_TABLE_GRID_HEIGHT = 640;
const USER_ANALYTICS_GRID_SLOT_PROPS = {
	pagination: {
		labelRowsPerPage: "",
		labelDisplayedRows: ({ from, to, count }) =>
			`${from}-${to} of ${count === -1 ? `more than ${to}` : count}`,
		SelectProps: {
			sx: { display: "none" },
		},
		sx: {
			"& .MuiTablePagination-selectLabel": { display: "none" },
			"& .MuiTablePagination-input": { display: "none" },
		},
	},
};
const USER_ANALYTICS_GRID_SX = {
	"& .MuiDataGrid-columnHeaderTitle": {
		fontSize: "13px",
		fontWeight: 700,
		lineHeight: 1.35,
		width: "100%",
		textAlign: "center",
	},
	"& .MuiDataGrid-columnHeaderTitleContainer": {
		justifyContent: "center",
	},
	"& .MuiDataGrid-cell": {
		fontSize: "13px",
		lineHeight: 1.35,
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		textAlign: "left",
	},
	"& .MuiDataGrid-cell > .MuiBox-root": {
		width: "100%",
		height: "100%",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
	},
	"& .MuiDataGrid-cell .MuiTypography-body1, & .MuiDataGrid-cell .MuiTypography-body2": {
		fontSize: "13px",
		lineHeight: 1.35,
		width: "100%",
		textAlign: "left",
	},
	"& .MuiDataGrid-cell .MuiTypography-caption": {
		fontSize: "12px",
		lineHeight: 1.3,
		width: "100%",
		textAlign: "left",
	},
};
const DASHBOARD_REFRESH_QUERY_KEYS = [
	"customer_id",
	"site_id",
	"legal_entity",
	"user_role",
	"user_email",
	"machine_id",
	"range",
	"startDate",
	"endDate",
];

const getDashboardRefreshScope = (previousQuery, nextQuery) => {
	if (!previousQuery) return "dashboard";
	const dashboardChanged = DASHBOARD_REFRESH_QUERY_KEYS.some(
		(key) => String(previousQuery?.[key] ?? "") !== String(nextQuery?.[key] ?? "")
	);
	if (dashboardChanged) return "dashboard";

	const userActivityChanged =
		String(previousQuery.userPage ?? "") !== String(nextQuery.userPage ?? "") ||
		String(previousQuery.userPageSize ?? "") !== String(nextQuery.userPageSize ?? "");
	const pageTrafficChanged =
		String(previousQuery.pageTrafficPage ?? "") !== String(nextQuery.pageTrafficPage ?? "") ||
		String(previousQuery.pageTrafficPageSize ?? "") !== String(nextQuery.pageTrafficPageSize ?? "");
	const bannerEngagementChanged =
		String(previousQuery.bannerEngagementPage ?? "") !== String(nextQuery.bannerEngagementPage ?? "") ||
		String(previousQuery.bannerEngagementPageSize ?? "") !== String(nextQuery.bannerEngagementPageSize ?? "");
	const promotionUsageChanged =
		String(previousQuery.promotionUsagePage ?? "") !== String(nextQuery.promotionUsagePage ?? "") ||
		String(previousQuery.promotionUsagePageSize ?? "") !== String(nextQuery.promotionUsagePageSize ?? "");
	const searchAnalyticsChanged =
		String(previousQuery.searchAnalyticsPage ?? "") !== String(nextQuery.searchAnalyticsPage ?? "") ||
		String(previousQuery.searchAnalyticsPageSize ?? "") !== String(nextQuery.searchAnalyticsPageSize ?? "");

	const changedTables = [
		userActivityChanged ? "userActivity" : null,
		pageTrafficChanged ? "pageTraffic" : null,
		bannerEngagementChanged ? "bannerEngagement" : null,
		promotionUsageChanged ? "promotionUsage" : null,
		searchAnalyticsChanged ? "searchAnalytics" : null,
	].filter(Boolean);
	if (changedTables.length > 1) return "tables";
	if (userActivityChanged) return "userActivity";
	if (pageTrafficChanged) return "pageTraffic";
	if (bannerEngagementChanged) return "bannerEngagement";
	if (promotionUsageChanged) return "promotionUsage";
	if (searchAnalyticsChanged) return "searchAnalytics";
	return "dashboard";
};

const isAllValue = (value) => {
	if (Array.isArray(value)) {
		return value.length === 0 || value.every((item) => isAllValue(item));
	}
	return value === undefined || value === null || value === "" || value === ALL_VALUE;
};

const arraysEqualUnordered = (a, b) => {
	const arrA = Array.isArray(a) ? a : [];
	const arrB = Array.isArray(b) ? b : [];
	if (arrA.length !== arrB.length) return false;
	const sortedA = [...arrA].sort();
	const sortedB = [...arrB].sort();
	return sortedA.every((value, index) => value === sortedB[index]);
};

const withAllOption = (options = []) => [
	ALL_OPTION,
	...options.filter((option) => option?.value !== ALL_VALUE),
];

const getSearchQuery = (inputValue, selectedOption) => {
	const query = String(inputValue || "").trim().toLowerCase();
	if (!query) return "";
	const selectedLabel = String(selectedOption?.label || "").trim().toLowerCase();
	return query === selectedLabel ? "" : query;
};

const normalizeCustomerNumber = (value) => String(value || "").trim().toLowerCase();

const normalizeSiteNumber = (value) => String(value || "").trim().toLowerCase();

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const normalizeMachineNumber = (value) => String(value || "").trim().toUpperCase();

const getSiteLegalEntity = (site = {}) =>
	String(
		site.site_legal_entity ??
		site.legal_entity ??
		site.siteLegalEntity ??
		site.site_meta?.site_legal_entity ??
		site.meta?.site_legal_entity ??
		""
	).trim();

const uniqueBy = (items = [], getKey) => {
	const seen = new Set();
	return items.filter((item) => {
		const key = getKey(item);
		if (!key || seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const getCustomerOptions = (customers = []) =>
	customers.map((customer) => ({
		value: customer.cus_id,
		label: `${customer.customer_name} - ${customer.customer_number}`,
		searchText: `${customer.customer_name} ${customer.customer_number}`.toLowerCase(),
	}));

const getSiteOptions = (sites = []) =>
	sites.map((site) => ({
		value: site.site_id,
		label: `${site.customer_site_name} - ${site.customer_site_number}`,
		searchText: `${site.customer_site_name} ${site.customer_site_number}`.toLowerCase(),
		customer_id: site.cus_id,
	}));

const uniqueMachinesByNumber = (machines = []) => {
	const seen = new Map();
	machines.forEach((machine) => {
		const key =
			normalizeMachineNumber(machine?.machine_number) ||
			normalizeMachineNumber(machine?.machine_name) ||
			String(machine?.machine_id || "");
		if (!key || seen.has(key)) return;
		seen.set(key, machine);
	});
	return Array.from(seen.values());
};

const getMachineOptions = (machines = []) =>
	uniqueMachinesByNumber(machines).map((machine) => ({
		key: `${machine.machine_id}_${machine.site_id}_${machine.cus_id}`,
		value: machine.machine_id,
		label: `${machine.machine_name} - ${machine.machine_number}`,
		machine_number: machine.machine_number,
		searchText: `${machine.machine_name} ${machine.machine_number}`.toLowerCase(),
	}));

const getUserOptions = (users = []) =>
	uniqueBy(
		users
			.filter((user) => normalizeEmail(user?.user_email))
			.map((user) => ({
				value: user.user_email,
				label: `${user.user_display_name} (${user.user_email})`,
				displayName: user.user_display_name,
				email: user.user_email,
				searchText: `${user.user_display_name} ${user.user_email}`.toLowerCase(),
				user_email: user.user_email,
			})),
		(option) => normalizeEmail(option.user_email)
	);

const getUserRoleLabel = (row = {}) =>
	String(
		row?.user_role_name ||
		row?.user_role ||
		row?.role?.role_name ||
		row?.role?.role_type ||
		row?.role_name ||
		row?.role_type ||
		""
	).trim();

const getRoleOptions = (roles = []) =>
	Array.from(
		new Map(
			(roles || [])
				.map((role) => String(role?.role_name || role?.role_type || "").trim())
				.filter(Boolean)
				.map((roleName) => [
					roleName.toLowerCase(),
					{
						value: roleName,
						label: roleName,
						searchText: roleName.toLowerCase(),
					},
				])
		).values()
	).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

const isApprovedUser = (row) => {
	const statusValue = row?.user_status ?? row?.status ?? null;
	if (Number(statusValue) === 1) return true;
	const normalized = String(statusValue || "").trim().toLowerCase();
	return normalized === "approved" || normalized === "active";
};

const isSuperAdminRow = (row) => Number(row?.user_id) === 1;

const isAdminRow = (row) => {
	if (isSuperAdminRow(row)) return true;
	const roleToken =
		row?.user_role_name ||
		row?.user_role ||
		row?.role?.role_name ||
		row?.role?.role_type ||
		row?.user_type ||
		"";
	const normalized = String(roleToken).trim().toLowerCase();
	return normalized === "admin" || normalized.includes("admin");
};

const getUserAllocatedSiteIds = (userRow) => {
	if (Array.isArray(userRow?.allocations) && userRow.allocations.length > 0) {
		return Array.from(
			new Set(
				userRow.allocations
					.map((allocation) => Number(allocation?.site_id))
					.filter((siteId) => Number.isFinite(siteId) && siteId > 0)
			)
		);
	}
	if (Array.isArray(userRow?.user_sites)) {
		return Array.from(
			new Set(
				userRow.user_sites
					.map((siteId) => Number(siteId))
					.filter((siteId) => Number.isFinite(siteId) && siteId > 0)
			)
		);
	}
	const rawSites = `${userRow?.user_sites || ""}`.trim();
	if (!rawSites) return [];
	return rawSites
		.split(",")
		.map((siteId) => Number(siteId))
		.filter((siteId) => Number.isFinite(siteId) && siteId > 0);
};

const getAccessibleSiteIdsForUser = (userRow, scopedSites) => {
	const allocatedIds = getUserAllocatedSiteIds(userRow);
	if (allocatedIds.length > 0) return allocatedIds;
	if (isAdminRow(userRow)) return scopedSites.map((site) => Number(site.site_id));
	return [];
};

const formatNumber = (value) => {
	if (value === null || value === undefined || value === "—") return "—";
	const numberValue = Number(value);
	if (!Number.isFinite(numberValue)) return "—";
	return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(numberValue);
};

const getNumberWithSuffix = (value) => {
	const match = String(value ?? "").trim().match(/^(-?[\d,]+(?:\.\d+)?)(.*)$/);
	if (!match) return null;
	const numberValue = Number(match[1].replace(/,/g, ""));
	if (!Number.isFinite(numberValue)) return null;
	return { numberValue, suffix: match[2] || "" };
};

const formatCompactMetricValue = (value) => {
	if (value === null || value === undefined || value === "—") return "—";
	const parsed = getNumberWithSuffix(value);
	if (!parsed || Math.abs(parsed.numberValue) < 1000) return String(value);
	const formatted = new Intl.NumberFormat("en-US", {
		notation: "compact",
		maximumFractionDigits: parsed.numberValue >= 10000 ? 0 : 1,
	}).format(parsed.numberValue);
	return `${formatted}${parsed.suffix}`;
};

const formatCompactNumber = (value) => formatCompactMetricValue(formatNumber(value));

const formatExactMetricValue = (value) => {
	const parsed = getNumberWithSuffix(value);
	if (!parsed) return String(value ?? "—");
	return `${formatNumber(parsed.numberValue)}${parsed.suffix}`;
};

const shouldShowExactTooltip = (value) => {
	if (value === null || value === undefined || value === "—" || value === "") return false;
	return String(value).trim().length > 0;
};

const compactNumbersInText = (text) => String(text || "").replace(
	/-?[\d,]+(?:\.\d+)?/g,
	(match) => formatCompactMetricValue(match)
);

const exactTooltipSlotProps = {
	tooltip: {
		sx: {
			fontSize: "12px",
			lineHeight: 1.35,
			px: 1,
			py: 0.55,
		},
	},
};

const ExactNumber = ({ value, suffix = "", align = "right" }) => (
	<Box sx={{ width: "100%", display: "flex", justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start" }}>
		<Tooltip title={`${formatNumber(value)}${suffix}`} arrow placement="top" slotProps={exactTooltipSlotProps}>
			<Typography variant="body2" noWrap sx={{ display: "inline-block", width: "fit-content", textAlign: align }}>
				{formatCompactNumber(value)}{suffix}
			</Typography>
		</Tooltip>
	</Box>
);

const formatDuration = (seconds) => {
	const totalSeconds = Math.max(0, Number(seconds || 0));
	const minutes = Math.floor(totalSeconds / 60);
	const remainingSeconds = Math.round(totalSeconds % 60);
	return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
};

const getActiveUsersHelper = (range) => {
	if (range?.key === "T") return "Active today";
	if (range?.key === "C") return "Active in custom range";
	if (range?.key === "W") return "Active in the last 7 days";
	if (range?.label) return `Active this ${String(range.label).toLowerCase()}`;
	return "Active in selected range";
};

const getDauMauHelper = (range) => {
	if (range?.key === "T") return "Today's daily active users / last 30 days monthly active users";
	if (range?.key === "W") return "Average daily active users in the last 7 days / monthly active users";
	if (range?.key === "M") return "Average daily active users in month / monthly active users";
	if (range?.key === "Q") return "Average daily active users in quarter / monthly active users";
	if (range?.key === "Y") return "Average daily active users in year / monthly active users";
	if (range?.key === "C") return "Average daily active users in custom range / monthly active users";
	return "Average daily active users / monthly active users";
};

const getCleanRoutePath = (path = "") => String(path || "").split(/[?#]/)[0] || "/";

const getRouteSegments = (path = "") =>
	getCleanRoutePath(path)
		.split("/")
		.map((segment) => segment.trim())
		.filter(Boolean);

const getFirstLabelValue = (...values) =>
	values
		.map((value) => String(value || "").trim())
		.find(Boolean) || "";

const buildMachineLookup = (machines = []) => {
	const lookup = new Map();
	machines.forEach((machine) => {
		const keys = [
			machine?.machine_id,
			machine?.machine_number,
			machine?.machine_name,
		];
		keys.forEach((key) => {
			const normalized = String(key || "").trim().toLowerCase();
			if (normalized && !lookup.has(normalized)) {
				lookup.set(normalized, machine);
			}
		});
	});
	return lookup;
};

const getMachineForRoute = (machineLookup, machineKey) =>
	machineLookup?.get?.(String(machineKey || "").trim().toLowerCase()) || null;

const normalizeTrafficDisplayKey = (value = "") =>
	String(value || "")
		.normalize("NFKC")
		.replace(/[\u200B-\u200D\uFEFF]/g, "")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

const getMachineDisplayLabel = (machine) => {
	const machineNumber = getFirstLabelValue(machine?.machine_number);
	const machineName = getFirstLabelValue(machine?.machine_name);
	if (machineNumber && machineName) return `Machine ${machineNumber} - ${machineName}`;
	if (machineNumber) return `Machine ${machineNumber}`;
	if (machineName) return `Machine ${machineName}`;
	return "";
};

const formatUnknownRouteLabel = (path = "") => {
	const [section = "Application"] = getRouteSegments(path);
	const sectionLabel = section
		.replace(/[-_]+/g, " ")
		.replace(/\b\w/g, (value) => value.toUpperCase())
		.trim();
	return sectionLabel ? `${sectionLabel} Page` : "Application Page";
};

const GENERIC_BACKEND_PAGE_LABELS = new Set([
	"Machine Details",
	"Part Details",
	"Machine Documents",
	"3D Model Viewer",
	"2D Model Viewer",
	"Order History Details",
]);

const getPageLabel = (rowOrPath, machineLookup = new Map()) => {
	const row = typeof rowOrPath === "object" && rowOrPath !== null ? rowOrPath : { path: rowOrPath };
	const backendLabel = getFirstLabelValue(row.label, row.page_label, row.pageLabel, row.display_name);
	if (backendLabel && !backendLabel.startsWith("/") && !GENERIC_BACKEND_PAGE_LABELS.has(backendLabel)) {
		return backendLabel;
	}

	const path = getFirstLabelValue(row.path, row.page);
	if (!path) return "Unknown Page";

	const cleanPath = getCleanRoutePath(path);
	const segments = getRouteSegments(cleanPath);
	const routeMachine = getMachineForRoute(machineLookup, row.machine_id || segments[1]);
	const machineLabel = getMachineDisplayLabel(routeMachine) || getMachineDisplayLabel(row);
	const orderNumber = getFirstLabelValue(row.order_number);
	const documentName = getFirstLabelValue(row.document_name);
	const labelText = [
		backendLabel,
		row.page_name,
		row.page_title,
		row.element_label,
		row.action_category,
		row.type,
	]
		.map((value) => String(value || "").trim().toLowerCase())
		.filter(Boolean)
		.join(" ");

	if (row.type === "document" || documentName) {
		return "Documents";
	}

	if (segments[0] === "order-received") {
		return orderNumber ? `Order Placed - ${orderNumber}` : "Order Placed";
	}
	if (segments[0] === "order-error") return "Order Error";
	if (segments[0] === "order-failure") return "Order Failed";
	if (segments[0] === "machines" && segments.length >= 2) {
		if (segments.length >= 3) return "Parts";
		return "Machines";
	}
	if (segments[0] === "documentation" && segments.length >= 2) {
		return "AMFMethod Documentation";
	}
	if (segments[0] === "model-viewer" && segments.length >= 2) {
		const machineNumber = getFirstLabelValue(
			row.machine_number,
			row.machineNumber,
			row.machine_no,
			row.machineNo,
			routeMachine?.machine_number,
			segments[1]
		);

		if (machineNumber) {
			return `3D Model - ${machineNumber}`;
		}

		return "3D Model";
	}

	if (segments[0] === "2d-model-viewer" && segments.length >= 2) {
		const machineNumber = getFirstLabelValue(
			row.machine_number,
			row.machineNumber,
			row.machine_no,
			row.machineNo,
			routeMachine?.machine_number,
			segments[1]
		);

		if (machineNumber) {
			return `2D Model - ${machineNumber}`;
		}

		return "2D Model";
	}
	if (segments[0] === "order-history" && segments.length >= 2) {
		return machineLabel ? `Order History - ${machineLabel}` : "Order History Details";
	}
	if (segments[0] === "finder" && segments.length >= 2) return "Part Finder";
	if (segments[0] === "training" || labelText.includes("premium video") || labelText.includes("video")) return "Training";
	if (segments[0] === "parts") return "Parts";

	const routeMap = {
		"/login": "Login",
		"/register": "Register",
		"/forgot-password": "Forgot Password",
		"/reset-password": "Reset Password",
		"/reset-password/:token": "Reset Password",
		"*": "Page Not Found",

		"/verify-email": "Verify Email",
		"/accept-eula": "Accept EULA Page",

		"/": "Home",
		"/home": "Home",

		"/machines": "My Machines",
		"/machines/:machineID": "Machine Details",
		"/machines/:machineID/:productid": "Part Details",

		"/recommendations": "Recommendations",
		"/cart": "Cart",
		"/checkout": "Checkout",

		"/finder": "Part Finder",

		"/order-received/:orderKey": "Order Placed",
		"/order-error/:orderKey": "Order Error",
		"/order-history": "Order History",
		"/bestellungen": "Order History",
		"/order-history/:machineID/:productID": "Order Product Details",
		"/order-history/:machineID": "Order Details",
		"/track-order": "Track Order",

		"/settings": "Settings",
		"/profile": "Profile",
		"/perfil": "Profile",

		"/admin": "Admin",
		"/rspl": "Recommended Spare Parts List",

		"/documentation": "Documentation",
		"/documentation/:machine_id": "Machine Documents",

		"/requests": "Customer Care Requests",
		"/support": "Support",
		"/soporte": "Support",

		"/saved_for_later": "Saved For Later",
		"/notifications": "Notifications",

		"/analytics": "Order Analytics",
		"/analytics/user": "User Analytics",

		"/services": "Services",
		"/service": "Service",
		"/service/digital-solutions": "Digital Solutions",
		"/service/digital-solutions/preventive-dashboard": "Preventive Dashboard",
		"/service/digital-solutions/predictive-dashboard": "Predictive Dashboard",
		"/service/digital-solutions/sustainable-oven-service-dashboard": "Sustainable Oven Service Dashboard",
		"/service/digital-solutions/machine-dashboard": "Machine Dashboard",
		"/service/cases": "Cases",
		"/service/workorders": "Work Orders",
		"/training": "Training",
		"/training/basic-training": "Basic Training",
		"/reports": "Reports",
		"/search-results": "Search Results",

		"/logout": "Logout",
		"/cerrar-sesión": "Logout",

		"/model-viewer": "3D Model Viewer",
		"/2d-model-viewer": "2D Model Viewer",
		"/public/model-check": "Public Model Check Page",

		"/help": "Help",
		"/parts": "Parts",
	};
	return routeMap[cleanPath] || formatUnknownRouteLabel(cleanPath);
};

const formatApiDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	return `${month}-${day}-${year}`;
};

const formatDateTime = (value) => {
	const date = value ? new Date(value) : null;
	if (!date || Number.isNaN(date.getTime())) return "-";
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	const time = date.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
	});
	return `${month}-${day}-${year} ${time}`;
};

const getTrendDirection = (trend = {}) => {
	const rawDirection = String(trend?.direction || trend?.trend || "").toLowerCase();
	if (["up", "success", "growth"].includes(rawDirection)) return "up";
	if (["down", "error"].includes(rawDirection)) return "down";
	return "flat";
};

const TrendIndicator = ({ trend = {} }) => {
	if (trend?.label) {
		return (
			<Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 1 }}>
				{trend.label}
			</Typography>
		);
	}
	const direction = getTrendDirection(trend);
	const isUp = direction === "up";
	const isDown = direction === "down";
	const Icon = isUp ? TrendingUpIcon : isDown ? TrendingDownIcon : TrendingFlatIcon;
	const color = isUp ? "success.main" : isDown ? "error.main" : "text.secondary";
	const change = Number.isFinite(Number(trend?.change)) ? Number(trend.change) : 0;
	const changeLabel = `${change > 0 ? "+" : ""}${formatCompactNumber(change)}%`;

	return (
		<Tooltip
			title={`${change > 0 ? "+" : ""}${formatNumber(change)}%`}
			arrow
			placement="top"
			slotProps={exactTooltipSlotProps}
		>
			<Stack direction="row" alignItems="center" spacing={0.35} sx={{ color }}>
				<Icon sx={{ fontSize: 20 }} />
				<Typography variant="caption" sx={{ color, fontWeight: 700, lineHeight: 1 }}>
					{changeLabel}
				</Typography>
			</Stack>
		</Tooltip>
	);
};

const getStartOfMonth = (date) => {
	const next = new Date(date);
	next.setDate(1);
	next.setHours(0, 0, 0, 0);
	return next;
};

const getEndOfMonth = (date) => {
	const next = new Date(date);
	next.setMonth(next.getMonth() + 1);
	next.setDate(0);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getStartOfQuarter = (date) => {
	const next = new Date(date);
	const startMonth = Math.floor(next.getMonth() / 3) * 3;
	next.setMonth(startMonth, 1);
	next.setHours(0, 0, 0, 0);
	return next;
};

const getEndOfQuarter = (date) => {
	const next = getStartOfQuarter(date);
	next.setMonth(next.getMonth() + 3);
	next.setDate(0);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getStartOfYear = (date) => {
	const next = new Date(date);
	next.setMonth(0, 1);
	next.setHours(0, 0, 0, 0);
	return next;
};

const getEndOfYear = (date) => {
	const next = new Date(date);
	next.setMonth(11, 31);
	next.setHours(23, 59, 59, 999);
	return next;
};

const getRangeForKey = (key) => {
	const currentDate = new Date();

	if (key === "T") {
		const startDate = new Date(currentDate);
		startDate.setHours(0, 0, 0, 0);
		const endDate = new Date(currentDate);
		endDate.setHours(23, 59, 59, 999);
		return { key, label: "Today", startDate, endDate };
	}

	if (key === "M") {
		return {
			key,
			label: "Month",
			startDate: getStartOfMonth(currentDate),
			endDate: getEndOfMonth(currentDate),
		};
	}

	if (key === "Q") {
		return {
			key,
			label: "Quarter",
			startDate: getStartOfQuarter(currentDate),
			endDate: getEndOfQuarter(currentDate),
		};
	}

	if (key === "Y") {
		return {
			key,
			label: "Year",
			startDate: getStartOfYear(currentDate),
			endDate: (() => {
				const endDate = new Date(currentDate);
				endDate.setHours(23, 59, 59, 999);
				return endDate;
			})(),
		};
	}

	return {
		key: "W",
		label: "Last 7 Days",
		startDate: (() => {
			const startDate = new Date(currentDate);
			startDate.setDate(startDate.getDate() - 6);
			startDate.setHours(0, 0, 0, 0);
			return startDate;
		})(),
		endDate: (() => {
			const endDate = new Date(currentDate);
			endDate.setHours(23, 59, 59, 999);
			return endDate;
		})(),
	};
};

const renderMetricLabel = (label = "") => {
	const parts = String(label).split(" / ");
	if (parts.length < 2) return label;

	return (
		<>
			{parts[0]}
			<Box component="span" sx={{ display: { xs: "block", md: "inline" } }}>
				{` / ${parts.slice(1).join(" / ")}`}
			</Box>
		</>
	);
};

const MetricCard = ({ label, value, helper, icon, trend, tooltipKey, compact = false, loading = false }) => {
	const displayValue = formatCompactMetricValue(value);
	const exactValue = formatExactMetricValue(value);
	const showExactTooltip = shouldShowExactTooltip(value);
	const helperValue = compactNumbersInText(helper);

	return (
	<Paper
		elevation={0}
		sx={{
			py: compact ? { xs: 1.5, sm: 1.6, md: 1.75 } : { xs: 1.75, sm: 1.9, md: 2 },
			px: compact ? { xs: 1.75, sm: 1.8, md: 2.25 } : { xs: 2, sm: 2.1, md: 3 },
			height: "100%",
			minHeight: compact ? 96 : 118,
			borderRadius: "10px",
			boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
			borderBottom: "4px solid transparent",
			backgroundColor: "background.paper",
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			gap: 2,
			transition: "all 0.3s ease",
			"&:hover": {
				boxShadow: "0 0 10px 2px rgba(0,0,0,0.16)",
				borderBottomColor: "primary.main",
			},
		}}
	>
		<Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
			<Stack
				direction="row"
				alignItems="flex-start"
				spacing={0.4}
				sx={{
					mb: 1,
					minHeight: {
						xs: 44, // enough for 2 lines on mobile
						sm: 36,
						md: 38,
					},
				}}
			>
				<Typography
					variant="h6"
					color="text.primary"
					sx={{
						lineHeight: 1.25,
						fontWeight: 500,
						letterSpacing: 0,
						flex: "0 1 auto",
						minWidth: 0,
						fontSize: compact
							? { xs: 14, sm: 13, md: 15 }
							: { xs: 16, sm: 14, md: 16 },
					}}
				>
					{renderMetricLabel(label)}
				</Typography>
				{tooltipKey && analyticsKpiTooltips.user[tooltipKey] ? (
					<Tooltip
						title={analyticsKpiTooltips.user[tooltipKey]}
						arrow
						placement="top"
						slotProps={{
							tooltip: {
								sx: {
									fontSize: "13px",
									lineHeight: 1.4,
									maxWidth: 360,
									px: 1.25,
									py: 0.8,
								},
							},
						}}
					>
						<IconButton
							size="small"
							aria-label={`${label} info`}
							sx={{
								width: 20,
								height: 20,
								flex: "0 0 auto",
								color: "primary.main",
								mt: -0.15,
								p: 0,
							}}
						>
							<InfoOutlinedIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				) : null}
			</Stack>
			{loading ? (
				<Box sx={{ width: compact ? 96 : 132, mt: 0.25, mb: 0.35 }}>
					<ChartLoading compact />
				</Box>
			) : (
				<Tooltip
					title={showExactTooltip ? exactValue : ""}
					arrow
					placement="top"
					slotProps={exactTooltipSlotProps}
				>
					<Typography
						variant="h5"
						color="primary"
						sx={{
							display: "inline-block",
							width: "fit-content",
							fontWeight: 600,
							lineHeight: 1.05,
							letterSpacing: 0,
							fontSize: compact
								? { xs: 18, sm: 18, md: 20 }
								: { xs: 22, sm: 21, md: 24 },
						}}
					>
						{displayValue}
					</Typography>
				</Tooltip>
			)}
			{loading && helper ? (
				<Box sx={{ width: compact ? 150 : 210, mt: 0.85 }}>
					<Skeleton variant="text" animation="wave" height={18} />
				</Box>
			) : helper ? (
				<Tooltip title={helperValue !== helper ? helper : ""} arrow placement="top">
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.85 }}>
						{helperValue}
					</Typography>
				</Tooltip>
			) : null}
		</Box>
		<Stack
			alignItems="flex-end"
			justifyContent="space-between"
			spacing={1}
			sx={{ alignSelf: "stretch", flex: "0 0 auto" }}
		>
			<Box
				sx={{
					width: compact ? 34 : 40,
					height: compact ? 34 : 40,
					borderRadius: 1.5,
					display: "grid",
					placeItems: "center",
					color: "text.secondary",
					bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
				}}
			>
				{icon}
			</Box>
			{loading ? (
				<Box sx={{ width: 70 }}>
					<ChartLoading compact />
				</Box>
			) : (
				<TrendIndicator trend={trend} />
			)}
		</Stack>
	</Paper>
	);
};

const SectionTitle = ({ children }) => (
	<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 2 }}>
		{children}
	</Typography>
);

const getMetricDisplay = (metric, fallback = "—") =>
	metric?.displayValue ?? metric?.value ?? fallback;

const getMetricHelper = (metric) =>
	metric?.status === "not_tracked" ? "not tracked" : metric?.subLabel || "";

const UserAnalytics = () => {
	const { user, hasPermission } = useAuth();
	const { selectedSite } = useSite();
	const { optionKeys } = useOptions();
	const enabledAdminModuleSet = useMemo(() => new Set(resolveEnabledAdminModules(optionKeys)), [optionKeys]);
	const isSuperAdminViewer = Number(user?.user_id) === 1;
	const analyticsTrackingRoleAccess = useMemo(() => {
		if (isSuperAdminViewer) return null;
		const raw = user?.role?.permissions?.analytics?.tracking_role_access || user?.permissions?.analytics?.tracking_role_access;
		return Array.isArray(raw) ? raw.map((roleName) => String(roleName || "").trim()).filter(Boolean) : null;
	}, [isSuperAdminViewer, user]);
	const analyticsTrackingRoleSet = useMemo(
		() => analyticsTrackingRoleAccess ? new Set(analyticsTrackingRoleAccess.map((roleName) => roleName.toLowerCase())) : null,
		[analyticsTrackingRoleAccess]
	);
	const canSeeAnalytics =
		canAccessUserAnalytics(hasPermission, user) &&
		(isSuperAdminViewer || enabledAdminModuleSet.has("ITEMS_TEXT.USER_ANALYTICS"));
	const isAdminUser = useMemo(() => {
		if (!user) return false;
		if (isSuperAdminViewer) return true;
		const roleToken =
			user?.role?.role_type ||
			user?.role?.role_name ||
			user?.role?.role ||
			user?.user_role_name ||
			user?.user_role ||
			"";
		return String(roleToken).trim().toLowerCase() === "admin";
	}, [isSuperAdminViewer, user]);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [dashboard, setDashboard] = useState({
		summary: {},
		topPages: [],
		topUsers: [],
		conversionFunnel: [],
		conversionSankey: {
			nodes: [],
			links: [],
		},
		pageAnalytics: {
			rows: [],
			total: 0,
			page: 1,
			pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			pageCount: 0,
		},
		userAnalytics: {
			rows: [],
			total: 0,
			page: 1,
			pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			pageCount: 0,
		},
		bannerEngagement: {
			rows: [],
			total: 0,
			page: 1,
			pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			pageCount: 0,
		},
		promotionUsage: {
			rows: [],
			total: 0,
			page: 1,
			pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			pageCount: 0,
		},
		searchAnalytics: {
			topTerms: [],
			noResultTerms: [],
			table: {
				rows: [],
				total: 0,
				page: 1,
				pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
				pageCount: 0,
			},
		},
		metrics: {},
		user_activity_log: [],
		range: {},
		filters: {},
		trends: {},
		meta: {},
	});
	const [selectedRange, setSelectedRange] = useState(() => getRangeForKey("W"));
	const [selectedDateRange, setSelectedDateRange] = useState(() => {
		const initialRange = getRangeForKey("W");
		return [initialRange.startDate, initialRange.endDate];
	});
	const [customers, setCustomers] = useState([]);
	const [sites, setSites] = useState([]);
	const [users, setUsers] = useState([]);
	const [roles, setRoles] = useState([]);
	const [machines, setMachines] = useState([]);
	const [customersOptions, setCustomersOptions] = useState([]);
	const [sitesOptions, setSitesOptions] = useState([]);
	const [usersOptions, setUsersOptions] = useState([]);
	const [rolesOptions, setRolesOptions] = useState([]);
	const [legalEntityOptions, setLegalEntityOptions] = useState([]);
	const [machinesOptions, setMachinesOptions] = useState([]);
	const [accessScope, setAccessScope] = useState({ allowAll: false, allowNone: false });
	const [allocatedSiteIds, setAllocatedSiteIds] = useState([]);
	const [filterLoading, setFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");
	const [customerInput, setCustomerInput] = useState("");
	const [legalEntityInput, setLegalEntityInput] = useState("");
	const [siteInput, setSiteInput] = useState("");
	const [roleInput, setRoleInput] = useState("");
	const [userInput, setUserInput] = useState("");
	const [machineInput, setMachineInput] = useState("");
	const [debouncedCustomerInput, setDebouncedCustomerInput] = useState("");
	const [debouncedLegalEntityInput, setDebouncedLegalEntityInput] = useState("");
	const [debouncedSiteInput, setDebouncedSiteInput] = useState("");
	const [debouncedRoleInput, setDebouncedRoleInput] = useState("");
	const [debouncedUserInput, setDebouncedUserInput] = useState("");
	const [debouncedMachineInput, setDebouncedMachineInput] = useState("");
	const [filters, setFilters] = useState({
		legal_entity: ALL_VALUE,
		customer_id: ALL_VALUE,
		site_id: ALL_VALUE,
		user_role: [],
		user_email: ALL_VALUE,
		machine_id: ALL_VALUE,
	});
	const [userPaginationModel, setUserPaginationModel] = useState({
		page: 0,
		pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
	});
	const [pageTrafficPaginationModel, setPageTrafficPaginationModel] = useState({
		page: 0,
		pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
	});
	const [bannerEngagementPaginationModel, setBannerEngagementPaginationModel] = useState({
		page: 0,
		pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
	});
	const [promotionUsagePaginationModel, setPromotionUsagePaginationModel] = useState({
		page: 0,
		pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
	});
	const [searchAnalyticsPaginationModel, setSearchAnalyticsPaginationModel] = useState({
		page: 0,
		pageSize: DEFAULT_USER_ANALYTICS_PAGE_SIZE,
	});
	const [userActivityLoading, setUserActivityLoading] = useState(false);
	const [pageTrafficLoading, setPageTrafficLoading] = useState(false);
	const [bannerEngagementLoading, setBannerEngagementLoading] = useState(false);
	const [promotionUsageLoading, setPromotionUsageLoading] = useState(false);
	const [searchAnalyticsLoading, setSearchAnalyticsLoading] = useState(false);
	const quickRangeClickRef = useRef(false);
	const dashboardQueryRef = useRef({ key: "", query: null });
	const dashboardRequestIdRef = useRef(0);
	const dashboardLoadTimerRef = useRef(null);
	const dashboardAbortRef = useRef(null);
	const isRefreshingDashboard = loading;
	const isPageTrafficRefreshing = loading || pageTrafficLoading;
	const isUserActivityRefreshing = loading || userActivityLoading;
	const isBannerEngagementRefreshing = loading || bannerEngagementLoading;
	const isPromotionUsageRefreshing = loading || promotionUsageLoading;
	const isSearchAnalyticsRefreshing = loading || searchAnalyticsLoading;

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedCustomerInput(customerInput), 200);
		return () => clearTimeout(timer);
	}, [customerInput]);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedLegalEntityInput(legalEntityInput), 200);
		return () => clearTimeout(timer);
	}, [legalEntityInput]);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSiteInput(siteInput), 200);
		return () => clearTimeout(timer);
	}, [siteInput]);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedRoleInput(roleInput), 200);
		return () => clearTimeout(timer);
	}, [roleInput]);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedUserInput(userInput), 200);
		return () => clearTimeout(timer);
	}, [userInput]);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedMachineInput(machineInput), 200);
		return () => clearTimeout(timer);
	}, [machineInput]);

	const baseSites = useMemo(() => {
		if (accessScope.allowNone) return [];
		if (accessScope.allowAll) return sites;
		if (allocatedSiteIds.length > 0) {
			return sites.filter((site) => allocatedSiteIds.includes(Number(site.site_id)));
		}
		return [];
	}, [accessScope.allowAll, accessScope.allowNone, allocatedSiteIds, sites]);

	const baseCustomers = useMemo(() => {
		const customerIds = new Set(
			baseSites
				.map((site) => Number(site?.cus_id))
				.filter((customerId) => Number.isFinite(customerId) && customerId > 0)
		);
		return customers.filter((customer) => customerIds.has(Number(customer.cus_id)));
	}, [baseSites, customers]);

	const areFiltersEqual = useCallback((a, b) =>
		a.legal_entity === b.legal_entity &&
		a.customer_id === b.customer_id &&
		a.site_id === b.site_id &&
		arraysEqualUnordered(a.user_role, b.user_role) &&
		a.user_email === b.user_email &&
		a.machine_id === b.machine_id,
		[]
	);

	const getSitesForMachine = useCallback((machineId) => {
		const parsedId = Number(machineId);
		if (!Number.isFinite(parsedId)) return [];
		const siteIds = new Set(
			machines
				.filter((machine) => Number(machine.machine_id) === parsedId)
				.map((machine) => Number(machine.site_id))
				.filter((siteId) => Number.isFinite(siteId))
		);
		return baseSites.filter((site) => siteIds.has(Number(site.site_id)));
	}, [baseSites, machines]);

	const computeScopedSites = useCallback((draftFilters) => {
		let scopedSites = baseSites;
		if (!isAllValue(draftFilters.site_id)) {
			const siteId = Number(draftFilters.site_id);
			scopedSites = scopedSites.filter((site) => Number(site.site_id) === siteId);
		}
		if (!isAllValue(draftFilters.legal_entity)) {
			scopedSites = scopedSites.filter(
				(site) => getSiteLegalEntity(site) === String(draftFilters.legal_entity).trim()
			);
		}
		if (!isAllValue(draftFilters.customer_id)) {
			const customerId = Number(draftFilters.customer_id);
			scopedSites = scopedSites.filter((site) => Number(site.cus_id) === customerId);
		}
		if (!isAllValue(draftFilters.machine_id)) {
			const sitesForMachine = getSitesForMachine(draftFilters.machine_id);
			const machineSiteIds = new Set(sitesForMachine.map((site) => Number(site.site_id)));
			scopedSites = scopedSites.filter((site) => machineSiteIds.has(Number(site.site_id)));
		}
		if (!isAllValue(draftFilters.user_email)) {
			const selectedUser = users.find(
				(userRow) => normalizeEmail(userRow.user_email) === normalizeEmail(draftFilters.user_email)
			);
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				return [];
			}
			const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
			const allowedSiteIds = new Set(userSiteIds);
			scopedSites = scopedSites.filter((site) => allowedSiteIds.has(Number(site.site_id)));
		}
		return scopedSites;
	}, [baseSites, getSitesForMachine, users]);

	const buildOptionsFromScope = useCallback((scopedSites) => {
		const siteIds = new Set(scopedSites.map((site) => Number(site.site_id)));
		const customerIds = new Set(
			scopedSites
				.map((site) => Number(site.cus_id))
				.filter((customerId) => Number.isFinite(customerId) && customerId > 0)
		);
		const scopedCustomers = baseCustomers.filter((customer) => customerIds.has(Number(customer.cus_id)));
		const scopedMachines = machines.filter((machine) => siteIds.has(Number(machine.site_id)));
		const legalEntities = Array.from(
			new Set(scopedSites.map(getSiteLegalEntity).filter(Boolean))
		);
		return {
			legalEntityOptions: legalEntities.map((entity) => ({
				value: entity,
				label: entity,
				searchText: entity.toLowerCase(),
			})),
			customerOptions: getCustomerOptions(scopedCustomers),
			siteOptions: getSiteOptions(scopedSites),
			machineOptions: getMachineOptions(scopedMachines),
			roleOptions: getRoleOptions(
				analyticsTrackingRoleSet
					? roles.filter((role) =>
						analyticsTrackingRoleSet.has(String(role?.role_name || role?.role_type || "").trim().toLowerCase())
					)
					: roles
			),
			userOptions: [],
		};
	}, [analyticsTrackingRoleSet, baseCustomers, machines, roles]);

	const getUserOptionsForScope = useCallback((scopedSites, draftFilters) => {
		const approvedUsers = users
			.filter((userRow) => !isSuperAdminRow(userRow))
			.filter((userRow) => isApprovedUser(userRow))
			.filter((userRow) =>
				!analyticsTrackingRoleSet ||
				analyticsTrackingRoleSet.has(String(getUserRoleLabel(userRow)).trim().toLowerCase())
			)
			.filter((userRow) =>
				isAllValue(draftFilters.user_role) ||
				(Array.isArray(draftFilters.user_role) ? draftFilters.user_role : [draftFilters.user_role])
					.map((value) => String(value).trim())
					.includes(String(getUserRoleLabel(userRow)).trim())
			);
		const adminUsers = approvedUsers.filter((userRow) => {
			const allocatedIds = getUserAllocatedSiteIds(userRow);
			return allocatedIds.length === 0 && isAdminRow(userRow);
		});
		const adminEmails = new Set(adminUsers.map((userRow) => normalizeEmail(userRow.user_email)));
		const nonAdminUsers = approvedUsers.filter(
			(userRow) => !adminEmails.has(normalizeEmail(userRow.user_email))
		);
		const siteIdsForScope = new Set(scopedSites.map((site) => Number(site.site_id)));
		const selectedSiteId = isAllValue(draftFilters.site_id) ? null : Number(draftFilters.site_id);
		const selectedCustomerId = isAllValue(draftFilters.customer_id)
			? null
			: Number(draftFilters.customer_id);
		const customerSiteIds = selectedCustomerId
			? baseSites
				.filter((site) => Number(site.cus_id) === selectedCustomerId)
				.map((site) => Number(site.site_id))
			: [];
		const requiredSiteIds = selectedSiteId
			? new Set([selectedSiteId])
			: selectedCustomerId
				? new Set(customerSiteIds)
				: siteIdsForScope;
		const scopedUsers = nonAdminUsers.filter((userRow) => {
			const userSiteIds = getUserAllocatedSiteIds(userRow);
			if (userSiteIds.length === 0) return false;
			return userSiteIds.some((siteId) => requiredSiteIds.has(Number(siteId)));
		});
		const combined = requiredSiteIds.size > 0 ? [...adminUsers, ...scopedUsers] : scopedUsers;
		return getUserOptions(combined);
	}, [analyticsTrackingRoleSet, baseSites, users]);

	const validateSelections = useCallback((draftFilters, options) => {
		const isValid = (value, list) =>
			isAllValue(value) || list.some((option) => String(option.value) === String(value));
		const nextFilters = { ...draftFilters };
		if (!isValid(nextFilters.customer_id, options.customerOptions)) nextFilters.customer_id = ALL_VALUE;
		if (!isValid(nextFilters.legal_entity, options.legalEntityOptions)) nextFilters.legal_entity = ALL_VALUE;
		if (!isValid(nextFilters.site_id, options.siteOptions)) nextFilters.site_id = ALL_VALUE;
		nextFilters.user_role = Array.isArray(nextFilters.user_role)
			? nextFilters.user_role.filter((value) =>
				options.roleOptions.some((option) => String(option.value) === String(value))
			)
			: [];
		if (!isValid(nextFilters.user_email, options.userOptions)) nextFilters.user_email = ALL_VALUE;
		if (!isValid(nextFilters.machine_id, options.machineOptions)) nextFilters.machine_id = ALL_VALUE;
		return nextFilters;
	}, []);

	const applyAutoSelection = useCallback((draftFilters) => {
		const nextFilters = { ...draftFilters };
		if (!isAllValue(nextFilters.site_id)) {
			const selectedSiteRow = baseSites.find(
				(site) => Number(site.site_id) === Number(nextFilters.site_id)
			);
			if (!selectedSiteRow) {
				nextFilters.site_id = ALL_VALUE;
			} else if (isAllValue(nextFilters.customer_id)) {
				nextFilters.customer_id = selectedSiteRow.cus_id ?? nextFilters.customer_id;
			}
		}
		if (!isAllValue(nextFilters.customer_id) && !isAllValue(nextFilters.site_id)) {
			const selectedSiteRow = baseSites.find(
				(site) => Number(site.site_id) === Number(nextFilters.site_id)
			);
			if (selectedSiteRow && Number(selectedSiteRow.cus_id) !== Number(nextFilters.customer_id)) {
				nextFilters.site_id = ALL_VALUE;
			}
		}
		if (!isAllValue(nextFilters.machine_id)) {
			const candidateSites = getSitesForMachine(nextFilters.machine_id);
			const candidateSiteIds = new Set(candidateSites.map((site) => Number(site.site_id)));
			if (candidateSites.length === 0) {
				nextFilters.machine_id = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site_id)) {
				if (!candidateSiteIds.has(Number(nextFilters.site_id))) {
					nextFilters.machine_id = ALL_VALUE;
				}
			} else if (!isAllValue(nextFilters.customer_id)) {
				const customerCandidates = candidateSites.filter(
					(site) => Number(site.cus_id) === Number(nextFilters.customer_id)
				);
				if (customerCandidates.length === 1) {
					nextFilters.site_id = customerCandidates[0].site_id;
					nextFilters.customer_id = customerCandidates[0].cus_id ?? nextFilters.customer_id;
				}
			} else if (candidateSites.length === 1) {
				nextFilters.site_id = candidateSites[0].site_id;
				nextFilters.customer_id = candidateSites[0].cus_id ?? nextFilters.customer_id;
			}
		}
		if (!isAllValue(nextFilters.user_email)) {
			const selectedUser = users.find(
				(userRow) => normalizeEmail(userRow.user_email) === normalizeEmail(nextFilters.user_email)
			);
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				nextFilters.user_email = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site_id)) {
				const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
				if (!userSiteIds.includes(Number(nextFilters.site_id))) {
					nextFilters.user_email = ALL_VALUE;
				}
			}
		}
		return nextFilters;
	}, [baseSites, getSitesForMachine, users]);

	const computeNextState = useCallback((inputFilters) => {
		let draftFilters = { ...inputFilters };
		for (let index = 0; index < 2; index += 1) {
			draftFilters = applyAutoSelection(draftFilters);
			const scopedSites = computeScopedSites(draftFilters);
			const options = buildOptionsFromScope(scopedSites);
			options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
			const validatedFilters = validateSelections(draftFilters, options);
			if (areFiltersEqual(draftFilters, validatedFilters)) {
				return { filters: validatedFilters, options };
			}
			draftFilters = validatedFilters;
		}
		const scopedSites = computeScopedSites(draftFilters);
		const options = buildOptionsFromScope(scopedSites);
		options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
		return { filters: draftFilters, options };
	}, [
		applyAutoSelection,
		areFiltersEqual,
		buildOptionsFromScope,
		computeScopedSites,
		getUserOptionsForScope,
		validateSelections,
	]);

	useEffect(() => {
		if (!selectedSite || !baseSites.length) return;
		const selectedSiteRow = baseSites.find(
			(site) => String(site.customer_site_number) === String(selectedSite)
		);
		if (!selectedSiteRow) return;
		resetDashboardPaginationPages();
		setFilters((prevFilters) => {
			if (
				String(prevFilters.site_id) === String(selectedSiteRow.site_id) &&
				String(prevFilters.customer_id) === String(selectedSiteRow.cus_id ?? prevFilters.customer_id)
			) {
				return prevFilters;
			}
			return {
				...prevFilters,
				customer_id: selectedSiteRow.cus_id ?? prevFilters.customer_id,
				site_id: selectedSiteRow.site_id,
				legal_entity: getSiteLegalEntity(selectedSiteRow) || prevFilters.legal_entity,
				user_email: ALL_VALUE,
				machine_id: ALL_VALUE,
			};
		});
	}, [selectedSite, baseSites]);

	useEffect(() => {
		const { filters: nextFilters, options } = computeNextState(filters);
		setCustomersOptions(withAllOption(options.customerOptions));
		setLegalEntityOptions(withAllOption(options.legalEntityOptions));
		setSitesOptions(withAllOption(options.siteOptions));
		setRolesOptions(withAllOption(options.roleOptions));
		setUsersOptions(withAllOption(options.userOptions));
		setMachinesOptions(withAllOption(options.machineOptions));
		if (!areFiltersEqual(filters, nextFilters)) {
			setFilters(nextFilters);
		}
	}, [areFiltersEqual, computeNextState, filters]);

	const filterOptionMap = useMemo(
		() => ({
			customer_id: customersOptions,
			legal_entity: legalEntityOptions,
			site_id: sitesOptions,
			user_role: rolesOptions,
			user_email: usersOptions,
			machine_id: machinesOptions,
		}),
		[customersOptions, legalEntityOptions, machinesOptions, rolesOptions, sitesOptions, usersOptions]
	);

	const getApiRange = (rangeKey) => {
		if (rangeKey === "T") return "1d";
		if (rangeKey === "W") return "7d";
		if (rangeKey === "M") return "30d";
		if (rangeKey === "Q") return "90d";
		if (rangeKey === "Y") return "365d";
		return "";
	};

	const buildDashboardQuery = (
		nextFilters = filters,
		nextRange = selectedRange,
		nextPaginationModel = userPaginationModel,
		nextPageTrafficPaginationModel = pageTrafficPaginationModel,
		nextBannerEngagementPaginationModel = bannerEngagementPaginationModel,
		nextPromotionUsagePaginationModel = promotionUsagePaginationModel,
		nextSearchAnalyticsPaginationModel = searchAnalyticsPaginationModel
	) => {
		// Build normalized filters using consistent utility function
		const normalizedFilters = FILTER_KEYS.reduce((query, filter) => {
			const value = nextFilters[filter.key];
			// Use utility function to check "All" value consistently
			if (filter.key === "user_role") {
				// Multi-select: pass the array of selected roles through as-is
				// so the server can query-parse it back into an array.
				query[filter.key] = isAllValueUtil(value) ? "" : value;
				return query;
			}
			query[filter.key] = isAllValueUtil(value) ? "" : String(value).trim();
			return query;
		}, {});

		// Get active filters for logging/debugging
		const activeFilters = getActiveFilters(nextFilters);
		if (Object.keys(activeFilters).length > 0) {
			console.debug("[Analytics] Query with active filters:", activeFilters);
		}

		return {
			...normalizedFilters,
			range: getApiRange(nextRange.key),
			startDate: formatApiDate(nextRange.startDate),
			endDate: formatApiDate(nextRange.endDate),
			userPage: (Number(nextPaginationModel?.page) || 0) + 1,
			userPageSize:
				Number(nextPaginationModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			pageTrafficPage: (Number(nextPageTrafficPaginationModel?.page) || 0) + 1,
			pageTrafficPageSize:
				Number(nextPageTrafficPaginationModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			bannerEngagementPage: (Number(nextBannerEngagementPaginationModel?.page) || 0) + 1,
			bannerEngagementPageSize:
				Number(nextBannerEngagementPaginationModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			promotionUsagePage: (Number(nextPromotionUsagePaginationModel?.page) || 0) + 1,
			promotionUsagePageSize:
				Number(nextPromotionUsagePaginationModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE,
			searchAnalyticsPage: (Number(nextSearchAnalyticsPaginationModel?.page) || 0) + 1,
			searchAnalyticsPageSize:
				Number(nextSearchAnalyticsPaginationModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE,
		};
	};
	const loadDashboard = async (query = buildDashboardQuery(), refreshScope = "dashboard") => {
		const requestId = dashboardRequestIdRef.current + 1;
		dashboardRequestIdRef.current = requestId;
		const shouldRefreshDashboard = refreshScope === "dashboard";
		const shouldRefreshPageTraffic =
			refreshScope === "pageTraffic" || refreshScope === "tables";
		const shouldRefreshUserActivity =
			refreshScope === "userActivity" || refreshScope === "tables";
		const shouldRefreshBannerEngagement =
			refreshScope === "bannerEngagement" || refreshScope === "tables";
		const shouldRefreshPromotionUsage =
			refreshScope === "promotionUsage" || refreshScope === "tables";
		const shouldRefreshSearchAnalytics =
			refreshScope === "searchAnalytics" || refreshScope === "tables";

		if (shouldRefreshDashboard) {
			setLoading(true);
			setPageTrafficLoading(false);
			setUserActivityLoading(false);
			setBannerEngagementLoading(false);
			setPromotionUsageLoading(false);
			setSearchAnalyticsLoading(false);
		} else {
			if (shouldRefreshPageTraffic) setPageTrafficLoading(true);
			if (shouldRefreshUserActivity) setUserActivityLoading(true);
			if (shouldRefreshBannerEngagement) setBannerEngagementLoading(true);
			if (shouldRefreshPromotionUsage) setPromotionUsageLoading(true);
			if (shouldRefreshSearchAnalytics) setSearchAnalyticsLoading(true);
		}
		setError("");
		if (dashboardAbortRef.current) {
			dashboardAbortRef.current.abort();
		}
		const abortController = new AbortController();
		dashboardAbortRef.current = abortController;
		try {
			const result = await getUserAnalyticsDashboard(query, { signal: abortController.signal });
			if (requestId !== dashboardRequestIdRef.current) return;
			if (result?.aborted) return;
			if (result?.success === false) {
				setError(result?.message || "Unable to load analytics data.");
				return;
			}
			setDashboard(result || {});
			if (result?.meta?.export?.truncated) {
				setError(
					"OpenPanel reached the configured export safety limit. Increase OPENPANEL_ANALYTICS_MAX_PAGES to include the remaining events."
				);
			}
		} catch (err) {
			if (err?.name === "AbortError") return;
			if (requestId === dashboardRequestIdRef.current) {
				setError(err?.message || "Unable to load analytics data.");
			}
		} finally {
			if (dashboardAbortRef.current === abortController) {
				dashboardAbortRef.current = null;
			}
			if (requestId === dashboardRequestIdRef.current) {
				if (shouldRefreshDashboard) setLoading(false);
				if (shouldRefreshPageTraffic) setPageTrafficLoading(false);
				if (shouldRefreshUserActivity) setUserActivityLoading(false);
				if (shouldRefreshBannerEngagement) setBannerEngagementLoading(false);
				if (shouldRefreshPromotionUsage) setPromotionUsageLoading(false);
				if (shouldRefreshSearchAnalytics) setSearchAnalyticsLoading(false);
			}
		}
	};

	useEffect(() => {
		const query = buildDashboardQuery(
			filters,
			selectedRange,
			userPaginationModel,
			pageTrafficPaginationModel,
			bannerEngagementPaginationModel,
			promotionUsagePaginationModel,
			searchAnalyticsPaginationModel
		);
		const queryKey = JSON.stringify(query);
		if (queryKey === dashboardQueryRef.current.key) return;
		const refreshScope = getDashboardRefreshScope(dashboardQueryRef.current.query, query);
		dashboardQueryRef.current = { key: queryKey, query };
		if (dashboardLoadTimerRef.current) {
			clearTimeout(dashboardLoadTimerRef.current);
			dashboardLoadTimerRef.current = null;
		}
		if (refreshScope === "dashboard") {
			dashboardLoadTimerRef.current = setTimeout(() => {
				dashboardLoadTimerRef.current = null;
				loadDashboard(query, refreshScope);
			}, 250);
			return () => {
				if (dashboardLoadTimerRef.current) {
					clearTimeout(dashboardLoadTimerRef.current);
					dashboardLoadTimerRef.current = null;
				}
			};
		}
		loadDashboard(query, refreshScope);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, selectedRange, userPaginationModel, pageTrafficPaginationModel, bannerEngagementPaginationModel, promotionUsagePaginationModel, searchAnalyticsPaginationModel]);

	useEffect(() => {
		let isMounted = true;

		const loadFilterOptions = async () => {
			if (!user) return;
			setFilterLoading(true);
			setFilterError("");

			let scope = { allowAll: Boolean(isAdminUser), allowNone: !isAdminUser };
			let siteIds = [];

			try {
				const allocationResponse = await getUserAllocations(user.user_id);
				const allocations = allocationResponse?.data?.allocations || [];
				siteIds = [
					...new Set(
						allocations
							.map((allocation) => Number(allocation.site_id))
							.filter((siteId) => Number.isFinite(siteId) && siteId > 0)
					),
				];
				const customerIds = [
					...new Set(
						allocations
							.map((allocation) => Number(allocation.cus_id))
							.filter((customerId) => Number.isFinite(customerId) && customerId > 0)
					),
				];
				const hasAllocations = siteIds.length > 0 || customerIds.length > 0;
				scope = {
					allowAll: isAdminUser && !hasAllocations,
					allowNone: !isAdminUser && !hasAllocations,
				};
			} catch (_) {
				scope = { allowAll: Boolean(isAdminUser), allowNone: !isAdminUser };
				siteIds = [];
			}

			try {
				if (!isMounted) return;
				setAccessScope(scope);
				setAllocatedSiteIds(siteIds);

				if (scope.allowNone) {
					setCustomers([]);
					setSites([]);
					setUsers([]);
					setRoles([]);
					setMachines([]);
					return;
				}

				const [customerResponse, siteResponse, userResponse, machineResponse, rolesResponse] = await Promise.all([
					getCustomersCusSites(),
					getAllCustomerSites(),
					getAllUsers(),
					getMachinesByProvision(),
					getRoles(),
				]);
				if (!isMounted) return;
				const errors = [];

				if (customerResponse?.status) {
					const allCustomers = (customerResponse.data || []).filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					const dedupedCustomers = uniqueBy(allCustomers, (customer) =>
						normalizeCustomerNumber(customer?.customer_number) || String(customer?.cus_id)
					);
					setCustomers(dedupedCustomers);
				} else {
					setCustomers([]);
					errors.push("Unable to load bakery filter options.");
				}

				if (siteResponse?.status) {
					const allSites = (siteResponse.data || []).filter(
						(site) => site.customer_site_name && site.customer_site_name !== "EMPTY"
					);
					const dedupedSites = uniqueBy(allSites, (site) =>
						normalizeSiteNumber(site?.customer_site_number || site?.site_number) || String(site?.site_id)
					);
					const scopedSites = scope.allowAll
						? dedupedSites
						: siteIds.length > 0
							? dedupedSites.filter((site) => siteIds.includes(Number(site.site_id)))
							: [];
					setSites(scopedSites);
				} else {
					setSites([]);
					errors.push("Unable to load bakery location filter options.");
				}

				if (Array.isArray(userResponse?.data)) {
					const dedupedUsers = uniqueBy(
						userResponse.data,
						(userRow) => normalizeEmail(userRow?.user_email) || String(userRow?.user_id || "")
					).filter((userRow) => !isSuperAdminRow(userRow));
					setUsers(dedupedUsers);
				} else {
					setUsers([]);
					errors.push("Unable to load user filter options.");
				}

				if (Array.isArray(rolesResponse?.data)) {
					setRoles(rolesResponse.data);
				} else {
					setRoles([]);
					errors.push("Unable to load user role filter options.");
				}

				if (machineResponse?.status) {
					const activeMachines = (machineResponse.data || []).filter(
						(machine) => String(machine?.machine_status || "").toLowerCase() === "active"
					);
					setMachines(activeMachines);
				} else {
					setMachines([]);
					errors.push("Unable to load machine filter options.");
				}

				if (errors.length > 0) {
					setFilterError(errors.join(" "));
				}
			} catch (err) {
				if (!isMounted) return;
				setCustomers([]);
				setSites([]);
				setUsers([]);
				setRoles([]);
				setMachines([]);
				setFilterError(`Unable to load analytics filter options: ${err.message}`);
			} finally {
				if (isMounted) {
					setFilterLoading(false);
				}
			}
		};

		loadFilterOptions();

		return () => {
			isMounted = false;
		};
	}, [isAdminUser, user]);

	const summaryCards = useMemo(
		() => {
			const core = dashboard.metrics?.core_engagement || {};
			const depth = dashboard.metrics?.engagement_depth || {};
			return [
				{
					label: "Active users",
					value: getMetricDisplay(core.active_users, formatNumber(dashboard.summary?.activeUsers ?? dashboard.meta?.rangeUsers)),
					helper: getMetricHelper(core.active_users) || getActiveUsersHelper(selectedRange),
					icon: <PeopleAltOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.activeUsers || dashboard.trends?.mau,
					tooltipKey: "activeUsers",
					featured: true,
				},
				{
					label: "Stickiness ratio",
					value: getMetricDisplay(core.stickiness_ratio, dashboard.summary?.dauMau === null ? "—" : `${formatNumber(dashboard.summary?.dauMau ?? dashboard.summary?.stickiness)}%`),
					helper: getMetricHelper(core.stickiness_ratio) || getDauMauHelper(selectedRange),
					icon: <TimelineOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.dauMau || dashboard.trends?.stickiness,
					tooltipKey: "dauMau",
					featured: true,
				},
				{
					label: "Avg. session duration",
					value: getMetricDisplay(core.avg_session_duration, formatDuration(dashboard.summary?.averageSessionDuration)),
					helper: getMetricHelper(core.avg_session_duration) || "Time per session",
					icon: <AccessTimeOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.averageSessionDuration,
					tooltipKey: "averageSessionDuration",
					featured: true,
				},
				{
					label: "Sessions / user",
					value: getMetricDisplay(depth.sessions_per_user, formatNumber(dashboard.summary?.averageSessionsPerUser)),
					helper: getMetricHelper(depth.sessions_per_user),
					icon: <TimelineOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.averageSessionsPerUser,
					tooltipKey: "averageSessionsPerUser",
				},
				{
					label: "Logins / user",
					value: getMetricDisplay(depth.logins_per_user, formatNumber(dashboard.summary?.loginFrequencyPerUser)),
					helper: getMetricHelper(depth.logins_per_user),
					icon: <LoginOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.loginFrequencyPerUser,
					tooltipKey: "loginFrequencyPerUser",
				},
				{
					label: "Page views / session",
					value: getMetricDisplay(depth.page_views_per_session, formatNumber(dashboard.summary?.pageViewsPerSession)),
					helper: getMetricHelper(depth.page_views_per_session),
					icon: <PageviewOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.pageViewsPerSession,
					tooltipKey: "pageViewsPerSession",
				},
				{
					label: "Page views / user",
					value: getMetricDisplay(depth.page_views_per_user, formatNumber(dashboard.summary?.pageViewsPerUser)),
					helper: getMetricHelper(depth.page_views_per_user),
					icon: <PageviewOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.pageViewsPerUser,
					tooltipKey: "pageViewsPerUser",
				},
				{
					label: "Searches Performed",
					value: getMetricDisplay(depth.searches_performed, formatNumber(dashboard.summary?.searches)),
					helper: getMetricHelper(depth.searches_performed),
					icon: <SearchOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.searches,
					tooltipKey: "searchesPerformed",
				},
				{
					label: "Top Search Terms",
					value: dashboard.searchAnalytics?.topTerms?.[0]?.search_term || "—",
					helper: dashboard.searchAnalytics?.topTerms?.[0]
						? `${formatNumber(dashboard.searchAnalytics.topTerms[0].count)} searches`
						: "No search terms yet",
					icon: <SearchOutlinedIcon fontSize="small" />,
					tooltipKey: "topSearchTerms",
				},
				{
					label: "Searches w/ No Results",
					value: getMetricDisplay(depth.searches_with_no_results, formatNumber(dashboard.summary?.searchesWithNoResults)),
					helper: getMetricHelper(depth.searches_with_no_results),
					icon: <SearchOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.searchesWithNoResults,
					tooltipKey: "searchesWithNoResults",
				},
				{
					label: "3D Model Opens",
					value: formatNumber(dashboard.metrics?.feature_funnel?.model_3d_opens ?? dashboard.summary?.model3dOpens),
					icon: <ViewInArOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.model3dOpens,
					tooltipKey: "model3dOpens",
				},
				{
					label: "2D Model Opens",
					value: formatNumber(dashboard.metrics?.feature_funnel?.model_2d_opens ?? dashboard.summary?.model2dOpens),
					icon: <ViewInArOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.model2dOpens,
					tooltipKey: "model2dOpens",
				},
			];
		},
		[dashboard.meta?.rangeUsers, dashboard.metrics, dashboard.searchAnalytics, dashboard.summary, dashboard.trends, selectedRange]
	);
	const featuredSummaryCards = useMemo(() => summaryCards.filter((card) => card.featured), [summaryCards]);
	const activitySummaryCards = useMemo(() => summaryCards.filter((card) => !card.featured), [summaryCards]);
	const documentSummaryCards = useMemo(
		() => {
			const content = dashboard.metrics?.content_engagement || {};
			return [
				{
					label: "Document downloads",
					value: formatNumber(content.document_downloads ?? dashboard.summary?.downloads),
					helper: "Downloaded documents",
					icon: <DownloadOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.downloads,
					tooltipKey: "downloads",
				},
				{
					label: "Documents recently viewed",
					value: formatNumber(content.recently_viewed ?? dashboard.summary?.recentlyViewedDocuments),
					helper: "Viewed documents",
					icon: <HistoryOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.recentlyViewedDocuments,
					tooltipKey: "recentlyViewedDocuments",
				},
				{
					label: "Favorites saved",
					value: formatNumber(content.favourites_saved ?? dashboard.summary?.favoriteDocuments),
					helper: "Net active Favorites",
					icon: <StarBorderOutlinedIcon fontSize="small" />,
					trend: dashboard.trends?.favoriteDocuments,
					tooltipKey: "favoriteDocuments",
				},
			];
		},
		[dashboard.metrics, dashboard.summary, dashboard.trends]
	);
	const marketingSummaryCards = useMemo(() => {
		const marketing = dashboard.metrics?.marketing_promotions || {};
		return [
			{
				label: "Banner clicks",
				value: formatNumber(marketing.banner_cta_clicks ?? dashboard.summary?.bannerClicks),
				helper: marketing.bannerSubLabel || "",
				icon: <MouseOutlinedIcon fontSize="small" />,
				trend: dashboard.trends?.bannerClicks,
			},
			{
				label: "Promo codes used",
				value: formatNumber(marketing.promo_codes_used ?? dashboard.summary?.ordersWithPromo),
				helper: marketing.promoSubLabel || "",
				icon: <ShoppingCartCheckoutOutlinedIcon fontSize="small" />,
				trend: dashboard.trends?.ordersWithPromo,
			},
		];
	}, [dashboard.metrics, dashboard.summary, dashboard.trends]);
	const funnelMetrics = dashboard.metrics?.feature_funnel || {};
	const getSankeyOption = useCallback((steps, links) => {
		const chartSteps = (steps || [])
			.map((step) => ({
				label: String(step?.label || "").trim(),
				value: Number(step?.value || 0),
			}))
			.filter((step) => step.label);
		const nodeNames = new Set(chartSteps.map((step) => step.label));
		const chartLinks = (links || [])
			.map((link) => ({
				source: String(link?.source || "").trim(),
				target: String(link?.target || "").trim(),
				value: Number(link?.value || 0),
			}))
			.filter((link) => nodeNames.has(link.source) && nodeNames.has(link.target) && link.value > 0);

		return {
			tooltip: {
				trigger: "item",
				triggerOn: "mousemove",
				valueFormatter: (value) => formatCompactNumber(value),
			},
			series: [
				{
					type: "sankey",
					data: chartSteps.map((step) => ({ name: step.label })),
					links: chartLinks,
					nodeWidth: 16,
					nodeGap: 18,
					right: 190,
					left: 16,
					top: 12,
					bottom: 12,
					emphasis: { focus: "adjacency" },
					lineStyle: {
						color: "gradient",
						curveness: 0.45,
					},
					label: {
						color: "#333",
						fontSize: 12,
						lineHeight: 16,
						overflow: "break",
						width: 170,
						formatter: ({ name }) => {
							const step = chartSteps.find((item) => item.label === name);
							return `${name}\n${formatCompactNumber(step?.value ?? 0)}`;
						},
					},
				},
			],
			media: [
				{
					query: { maxWidth: 640 },
					option: {
						series: [
							{
								right: 120,
								left: 8,
								label: { width: 105 },
							},
						],
					},
				},
			],
			hasData: chartSteps.length > 1 && chartLinks.length > 0,
		};
	}, []);
	const orderFunnelChartOption = useMemo(
		() => getSankeyOption(
			(dashboard.conversionSankey?.nodes || []).map((node) => ({
				label: node.name,
				value: (dashboard.conversionFunnel || []).find((step) => step.label === node.name)?.sessions || 0,
			})),
			dashboard.conversionSankey?.links || []
		),
		[dashboard.conversionFunnel, dashboard.conversionSankey, getSankeyOption]
	);
	const getMetricNumber = (...values) => {
		for (const value of values) {
			const numberValue = Number(value);
			if (Number.isFinite(numberValue)) return numberValue;
		}
		return 0;
	};

	const twoDModelChartOption = useMemo(() => {
		const twoDClicks = getMetricNumber(
			funnelMetrics.model_2d_clicks,
			funnelMetrics.two_d_model_clicks,
			funnelMetrics.model2dClicks,
			dashboard.summary?.model2dClicks,
			funnelMetrics.model_2d_opens,
			dashboard.summary?.model2dOpens
		);

		const twoDOpens = getMetricNumber(
			funnelMetrics.model_2d_opens,
			dashboard.summary?.model2dOpens
		);

		const checkoutOrders = getMetricNumber(
			funnelMetrics.model_2d_checkout_steps,
			funnelMetrics.model_2d_checkout_orders,
			funnelMetrics.checkout_steps,
			dashboard.summary?.checkoutSteps
		);

		const steps = [
			{ label: "2D Clicks", value: twoDClicks },
			{ label: "2D Model Opens", value: twoDOpens },
			{ label: "Checkout / Order", value: checkoutOrders },
		];

		return getSankeyOption(steps, [
			{
				source: steps[0].label,
				target: steps[1].label,
				value: Math.max(0, Math.min(steps[0].value, steps[1].value)),
			},
			{
				source: steps[1].label,
				target: steps[2].label,
				value: Math.max(0, Math.min(steps[1].value, steps[2].value)),
			},
		]);
	}, [dashboard.summary, funnelMetrics, getSankeyOption]);

	const threeDModelChartOption = useMemo(() => {
		const threeDClicks = getMetricNumber(
			funnelMetrics.model_3d_clicks,
			funnelMetrics.three_d_model_clicks,
			funnelMetrics.model3dClicks,
			dashboard.summary?.model3dClicks,
			funnelMetrics.model_3d_opens,
			dashboard.summary?.model3dOpens
		);

		const threeDOpens = getMetricNumber(
			funnelMetrics.model_3d_opens,
			dashboard.summary?.model3dOpens
		);

		const checkoutOrders = getMetricNumber(
			funnelMetrics.model_3d_checkout_steps,
			funnelMetrics.model_3d_checkout_orders,
			funnelMetrics.checkout_steps,
			dashboard.summary?.checkoutSteps
		);

		const steps = [
			{ label: "3D Clicks", value: threeDClicks },
			{ label: "3D Model Opens", value: threeDOpens },
			{ label: "Checkout / Order", value: checkoutOrders },
		];

		return getSankeyOption(steps, [
			{
				source: steps[0].label,
				target: steps[1].label,
				value: Math.max(0, Math.min(steps[0].value, steps[1].value)),
			},
			{
				source: steps[1].label,
				target: steps[2].label,
				value: Math.max(0, Math.min(steps[1].value, steps[2].value)),
			},
		]);
	}, [dashboard.summary, funnelMetrics, getSankeyOption]);
	const dashboardSubtitle = useMemo(() => {
		const selectedCustomer = customersOptions.find((option) => String(option.value) === String(filters.customer_id));
		const selectedSiteOption = sitesOptions.find((option) => String(option.value) === String(filters.site_id));
		return [
			!isAllValue(filters.customer_id) ? selectedCustomer?.label : null,
			!isAllValue(filters.site_id) ? selectedSiteOption?.label : null,
			`${formatDisplayDate(selectedRange.startDate)} - ${formatDisplayDate(selectedRange.endDate)}`,
		].filter(Boolean).join(" · ");
	}, [customersOptions, filters.customer_id, filters.site_id, selectedRange.endDate, selectedRange.startDate, sitesOptions]);

	const resetDashboardPaginationPages = () => {
		setUserPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
		setPageTrafficPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
		setBannerEngagementPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
		setPromotionUsagePaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
		setSearchAnalyticsPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
	};

	const handleUserPaginationModelChange = (nextModel) => {
		const nextPage = Math.max(0, Number(nextModel?.page) || 0);
		const nextPageSize =
			Number(nextModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE;
		setUserPaginationModel((prev) => {
			const page = prev.pageSize === nextPageSize ? nextPage : 0;
			if (prev.page === page && prev.pageSize === nextPageSize) return prev;
			return {
				page,
				pageSize: nextPageSize,
			};
		});
	};

	const handlePageTrafficPaginationModelChange = (nextModel) => {
		const nextPage = Math.max(0, Number(nextModel?.page) || 0);
		const nextPageSize =
			Number(nextModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE;
		setPageTrafficPaginationModel((prev) => {
			const page = prev.pageSize === nextPageSize ? nextPage : 0;
			if (prev.page === page && prev.pageSize === nextPageSize) return prev;
			return {
				page,
				pageSize: nextPageSize,
			};
		});
	};

	const handleBannerEngagementPaginationModelChange = (nextModel) => {
		const nextPage = Math.max(0, Number(nextModel?.page) || 0);
		const nextPageSize =
			Number(nextModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE;
		setBannerEngagementPaginationModel((prev) => {
			const page = prev.pageSize === nextPageSize ? nextPage : 0;
			if (prev.page === page && prev.pageSize === nextPageSize) return prev;
			return {
				page,
				pageSize: nextPageSize,
			};
		});
	};

	const handlePromotionUsagePaginationModelChange = (nextModel) => {
		const nextPage = Math.max(0, Number(nextModel?.page) || 0);
		const nextPageSize =
			Number(nextModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE;
		setPromotionUsagePaginationModel((prev) => {
			const page = prev.pageSize === nextPageSize ? nextPage : 0;
			if (prev.page === page && prev.pageSize === nextPageSize) return prev;
			return {
				page,
				pageSize: nextPageSize,
			};
		});
	};

	const handleSearchAnalyticsPaginationModelChange = (nextModel) => {
		const nextPage = Math.max(0, Number(nextModel?.page) || 0);
		const nextPageSize =
			Number(nextModel?.pageSize) || DEFAULT_USER_ANALYTICS_PAGE_SIZE;
		setSearchAnalyticsPaginationModel((prev) => {
			const page = prev.pageSize === nextPageSize ? nextPage : 0;
			if (prev.page === page && prev.pageSize === nextPageSize) return prev;
			return {
				page,
				pageSize: nextPageSize,
			};
		});
	};

	const handleFilterChange = (key, value) => {
		resetDashboardPaginationPages();
		const nextValue = key === "user_role"
			? (Array.isArray(value) ? value : [])
			: (isAllValue(value) ? ALL_VALUE : value);
		if (key === "customer_id") {
			setSiteInput("");
			setUserInput("");
			setMachineInput("");
		}
		const selectedSite =
			key === "site_id" && !isAllValue(nextValue)
				? sites.find((site) => String(site.site_id) === String(nextValue))
				: null;
		if (selectedSite?.cus_id) {
			const selectedCustomer = customers.find(
				(customer) => String(customer.cus_id) === String(selectedSite.cus_id)
			);
			if (selectedCustomer) {
				setCustomerInput(`${selectedCustomer.customer_name} - ${selectedCustomer.customer_number}`);
			}
		}
		setFilters((prevFilters) => {
			const nextFilters = {
				...prevFilters,
				[key]: nextValue,
			};

			if (key === "customer_id") {
				nextFilters.site_id = ALL_VALUE;
				nextFilters.user_email = ALL_VALUE;
				nextFilters.machine_id = ALL_VALUE;
			}

			if (key === "legal_entity") {
				nextFilters.customer_id = ALL_VALUE;
				nextFilters.site_id = ALL_VALUE;
				nextFilters.user_email = ALL_VALUE;
				nextFilters.machine_id = ALL_VALUE;
			}

			if (key === "user_role") {
				nextFilters.user_email = ALL_VALUE;
			}

			if (key === "site_id" && !isAllValue(nextValue)) {
				if (selectedSite?.cus_id) {
					nextFilters.customer_id = selectedSite.cus_id;
				}
			}

			return nextFilters;
		});
	};

	const handleDateRangeClick = (key) => {
		const nextRange = getRangeForKey(key);
		resetDashboardPaginationPages();
		quickRangeClickRef.current = true;
		setSelectedRange(nextRange);
		setSelectedDateRange([nextRange.startDate, nextRange.endDate]);
	};

	const machineLookup = useMemo(() => buildMachineLookup(machines), [machines]);

	const pageTrafficRows = useMemo(() => {
		const rows = dashboard.pageAnalytics?.rows?.length
			? dashboard.pageAnalytics.rows
			: dashboard.topPages || [];

		const mergedRows = new Map();

		rows.forEach((row, index) => {
			const page = getPageLabel(row, machineLookup);
			const mergeKey = normalizeTrafficDisplayKey(page || row.label || row.path || `page-traffic-${index}`);
			const views = Number(row.views || 0);
			const users = Number(row.users || row.uniqueUsers || 0);
			const sessions = Number(row.sessions || 0);

			if (!mergedRows.has(mergeKey)) {
				mergedRows.set(mergeKey, {
					...row,
					page_traffic_key: `page-traffic-${mergeKey || index}`,
					page,
					views,
					users,
					sessions,
				});
				return;
			}

			const existing = mergedRows.get(mergeKey);
			existing.views += views;
			existing.users = Math.max(Number(existing.users || 0), users);
			existing.sessions += sessions;
		});

		const totalViews = Array.from(mergedRows.values()).reduce(
			(sum, row) => sum + Number(row.views || 0),
			0
		);

		return Array.from(mergedRows.values()).map((row) => ({
			...row,
			percentage: totalViews ? Number(((Number(row.views || 0) / totalViews) * 100).toFixed(2)) : 0,
		}));
	}, [dashboard.pageAnalytics, dashboard.topPages, machineLookup]);

	const rawPageTrafficRows = dashboard.pageAnalytics?.rows?.length
		? dashboard.pageAnalytics.rows
		: dashboard.topPages || [];
	const hasMergedPageTrafficRows = pageTrafficRows.length < rawPageTrafficRows.length;
	const pageTrafficRowCount = hasMergedPageTrafficRows
		? pageTrafficRows.length
		: Number(dashboard.pageAnalytics?.total ?? pageTrafficRows.length) || 0;

	const pageTrafficColumns = useMemo(
		() => [
			{
				field: "page",
				headerName: "Page",
				flex: 1.4,
				minWidth: 220,
				sortable: false,
				hideable: false,
				renderCell: (params) => (
					<Tooltip title={params.row.page || "Unknown Page"} arrow>
						<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
							{params.row.page || "Unknown Page"}
						</Typography>
					</Tooltip>
				),
			},
			{
				field: "views",
				headerName: "Views",
				type: "number",
				width: 110,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => <ExactNumber value={params.row.views} />,
			},
			{
				field: "percentage",
				headerName: "% of Total",
				width: 150,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => {
					const percentage = Number(params.row.percentage || 0);
					return (
						<Box sx={{ width: "100%", minWidth: 112 }}>
							<Box sx={{ mb: 0.5 }}>
								<ExactNumber value={percentage} suffix="%" />
							</Box>
							<LinearProgress
								variant="determinate"
								value={Math.min(100, percentage)}
								sx={{ height: 8, borderRadius: 999 }}
							/>
						</Box>
					);
				},
			},
		],
		[]
	);

	const userAnalyticsRows = useMemo(() => {
		const rows = dashboard.user_activity_log?.length
			? dashboard.user_activity_log
			: dashboard.userAnalytics?.rows?.length
				? dashboard.userAnalytics.rows
				: dashboard.topUsers || [];

		return rows.map((row, index) => ({
			...row,
			pageViews: row.pageViews ?? row.page_views ?? 0,
			lastSeen: row.lastSeen ?? row.last_seen ?? null,
			analytics_user_key:
				row.analytics_user_key ||
				String(row.user_id || row.user_email || `analytics-user-${index}`),
		}));
	}, [dashboard.topUsers, dashboard.userAnalytics, dashboard.user_activity_log]);

	const userAnalyticsRowCount =
		Number(dashboard.userAnalytics?.total ?? userAnalyticsRows.length) || 0;

	const userAnalyticsColumns = useMemo(
		() => [
			{
				field: "user_name",
				headerName: "User",
				flex: 1.4,
				minWidth: 220,
				sortable: false,
				hideable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
							{params.row.user_name || params.row.user_email || params.row.user_id || "Unknown User"}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{params.row.user_email || params.row.user_id || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "customer_name",
				headerName: "Bakery / location",
				flex: 1.25,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" noWrap>
							{params.row.bakery_location || params.row.customer_name || params.row.customer_number || "-"}
						</Typography>

						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{
								params.row.customer_site_number ||
								params.row.site_number ||
								params.row.site_name ||
								params.row.location ||
								"-"
							}
						</Typography>
					</Box>
				),
			},
			{
				field: "pageViews",
				headerName: "Page Views",
				type: "number",
				width: 120,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => <ExactNumber value={params.row.pageViews} />,
			},
			{
				field: "logins",
				headerName: "Logins",
				type: "number",
				width: 100,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => <ExactNumber value={params.row.logins} />,
			},
			{
				field: "lastSeen",
				headerName: "Last Seen",
				minWidth: 170,
				flex: 0.9,
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: (params) => (
					<Typography variant="body2" noWrap sx={{ width: "100%", textAlign: "center" }}>
						{formatDateTime(params.row.lastSeen)}
					</Typography>
				),
			},
		],
		[]
	);

	const bannerEngagementRows = useMemo(
		() =>
			(dashboard.bannerEngagement?.rows || []).map((row, index) => ({
				...row,
				banner_engagement_key:
					row.banner_engagement_key ||
					`banner-engagement-${row.user_id || row.user_email || index}-${row.banner_id || row.banner_title || index}`,
			})),
		[dashboard.bannerEngagement]
	);

	const bannerEngagementRowCount =
		Number(dashboard.bannerEngagement?.total ?? bannerEngagementRows.length) || 0;

	const promotionUsageRows = useMemo(
		() =>
			(dashboard.promotionUsage?.rows || []).map((row, index) => ({
				...row,
				promotion_usage_key:
					row.promotion_usage_key ||
					`promotion-usage-${row.user_id || row.user_email || index}-${row.promo_code || index}`,
			})),
		[dashboard.promotionUsage]
	);

	const promotionUsageRowCount =
		Number(dashboard.promotionUsage?.total ?? promotionUsageRows.length) || 0;

	const searchAnalyticsRows = useMemo(() => {
		const rows = Array.isArray(dashboard.searchAnalytics?.table?.rows)
			? dashboard.searchAnalytics.table.rows
			: dashboard.searchAnalytics?.topTerms || [];

		return rows.map((row, index) => ({
			...row,
			search_analytics_key:
				row.search_analytics_key ||
				`search-analytics-${row.normalized_search_term || row.search_term || index}`,
			average_result_count: Number(row.count || 0)
				? Number(row.result_count || 0) / Number(row.count || 1)
				: 0,
		}));
	}, [dashboard.searchAnalytics]);

	const searchAnalyticsRowCount =
		Number(dashboard.searchAnalytics?.table?.total ?? searchAnalyticsRows.length) || 0;

	const searchAnalyticsColumns = useMemo(
		() => [
			{
				field: "search_term",
				headerName: "Search Term",
				flex: 1.4,
				minWidth: 220,
				sortable: false,
				hideable: false,
				renderCell: (params) => (
					<Tooltip title={params.row.search_term || "-"} arrow>
						<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
							{params.row.search_term || "-"}
						</Typography>
					</Tooltip>
				),
			},
			{
				field: "count",
				headerName: "Searches",
				type: "number",
				width: 120,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => <ExactNumber value={params.row.count} />,
			},
			{
				field: "average_result_count",
				headerName: "Avg Results",
				type: "number",
				width: 130,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => (
					<ExactNumber value={Number(Number(params.row.average_result_count || 0).toFixed(1))} />
				),
			},
			{
				field: "page_source",
				headerName: "Source",
				flex: 1,
				minWidth: 170,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" noWrap>
							{params.row.page_source || "-"}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{params.row.page_path || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "latest_search_time",
				headerName: "Last Search",
				minWidth: 170,
				flex: 0.85,
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: (params) => (
					<Typography variant="body2" noWrap sx={{ width: "100%", textAlign: "center" }}>
						{formatDateTime(params.row.latest_search_time)}
					</Typography>
				),
			},
		],
		[]
	);

	const bannerEngagementColumns = useMemo(
		() => [
			{
				field: "site_name",
				headerName: "Bakery / Location",
				flex: 1.15,
				minWidth: 190,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" noWrap>
							{params.row.customer_name || params.row.customer_number || "-"}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{
								params.row.customer_site_number ||
								params.row.site_number ||
								params.row.site_name ||
								params.row.location ||
								"-"
							}
						</Typography>
					</Box>
				),
			},
			{
				field: "user_name",
				headerName: "User",
				flex: 1.25,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
							{params.row.user_name || params.row.user_email || "Unknown User"}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{params.row.user_email || params.row.user_id || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "banner_title",
				headerName: "Banner",
				flex: 1.3,
				minWidth: 220,
				sortable: false,
				hideable: false,
				renderCell: (params) => (
					<Tooltip title={params.row.banner_title || "Untitled Banner"} arrow>
						<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
							{params.row.banner_title || "Untitled Banner"}
						</Typography>
					</Tooltip>
				),
			},
			{
				field: "ctaClicks",
				headerName: "Banner Clicks",
				type: "number",
				width: 115,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => <ExactNumber value={params.row.ctaClicks} />,
			},
			{
				field: "dismissed",
				headerName: "Banner Closed",
				type: "number",
				width: 115,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => <ExactNumber value={params.row.dismissed} />,
			},
			{
				field: "lastActivity",
				headerName: "Last Activity",
				minWidth: 170,
				flex: 0.85,
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: (params) => (
					<Typography variant="body2" noWrap sx={{ width: "100%", textAlign: "center" }}>
						{formatDateTime(params.row.lastActivity)}
					</Typography>
				),
			},
		],
		[]
	);

	const promotionUsageColumns = useMemo(
		() => [

			{
				field: "site_name",
				headerName: "Bakery / Location",
				flex: 1.1,
				minWidth: 190,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" noWrap>
							{params.row.customer_name || "-"}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{params.row.site_name || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "user_name",
				headerName: "User",
				flex: 1.2,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => (
					<Box sx={{ minWidth: 0, py: 0.5 }}>
						<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
							{params.row.user_name || params.row.user_email || "Unknown User"}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
							{params.row.user_email || params.row.user_id || "-"}
						</Typography>
					</Box>
				),
			},
			{
				field: "promo_code",
				headerName: "Promo Code",
				minWidth: 145,
				flex: 0.8,
				sortable: false,
				hideable: false,
				renderCell: (params) => (
					<Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
						{params.row.promo_code || "-"}
					</Typography>
				),
			},
			{
				field: "original_price",
				headerName: "Original Price",
				type: "number",
				width: 135,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => (
					<ExactNumber
						value={params.row.original_price ?? params.row.price_before_discount ?? 0}
					/>
				),
			},
			{
				field: "discounted_price",
				headerName: "Discounted Price",
				type: "number",
				width: 150,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => (
					<ExactNumber
						value={params.row.discounted_price ?? params.row.price_after_discount ?? 0}
					/>
				),
			},
			{
				field: "discount_percentage",
				headerName: "Discount %",
				type: "number",
				width: 120,
				align: "right",
				headerAlign: "right",
				sortable: false,
				renderCell: (params) => {
					const value = Number(params.row.discount_percentage ?? 0);
					return <ExactNumber value={value} suffix="%" />;
				},
			},
			{
				field: "order_number",
				headerName: "Sales Orders",
				minWidth: 150,
				flex: 0.8,
				sortable: false,
				renderCell: (params) => (
					<Typography variant="body2" noWrap>
						{params.row.order_number || params.row.reason || "-"}
					</Typography>
				),
			},
			{
				field: "lastActivity",
				headerName: "Last Activity",
				minWidth: 170,
				flex: 0.85,
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: (params) => (
					<Typography variant="body2" noWrap sx={{ width: "100%", textAlign: "center" }}>
						{formatDateTime(params.row.lastActivity)}
					</Typography>
				),
			},
		],
		[]
	);

	const handleDateRangePickerChange = (nextDateRange) => {
		setSelectedDateRange(nextDateRange);

		if (quickRangeClickRef.current) {
			quickRangeClickRef.current = false;
			return;
		}

		if (!Array.isArray(nextDateRange)) return;

		const [rawStartDate, rawEndDate] = nextDateRange;
		const startDate = rawStartDate instanceof Date ? rawStartDate : new Date(rawStartDate);
		const endDate = rawEndDate instanceof Date ? rawEndDate : new Date(rawEndDate);

		if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

		const normalizedEndDate = startDate > endDate ? new Date(startDate) : endDate;
		normalizedEndDate.setHours(23, 59, 59, 999);

		const nextRange = {
			key: "C",
			label: "Custom",
			startDate,
			endDate: normalizedEndDate,
		};

		resetDashboardPaginationPages();
		setSelectedRange(nextRange);
	};
	const handleReset = () => {
		resetDashboardPaginationPages();
		setFilters({
			legal_entity: ALL_VALUE,
			customer_id: ALL_VALUE,
			site_id: ALL_VALUE,
			user_role: [],
			user_email: ALL_VALUE,
			machine_id: ALL_VALUE,
		});
		setLegalEntityInput("");
		setCustomerInput("");
		setSiteInput("");
		setRoleInput("");
		setUserInput("");
		setMachineInput("");
	};

	const handleClearFilter = (key) => {
		resetDashboardPaginationPages();
		setFilters((prevFilters) => ({
			...prevFilters,
			[key]: key === "user_role" ? [] : ALL_VALUE,
			...(key === "customer_id"
				? { site_id: ALL_VALUE, user_email: ALL_VALUE, machine_id: ALL_VALUE }
				: {}),
			...(key === "legal_entity"
				? { customer_id: ALL_VALUE, site_id: ALL_VALUE, user_email: ALL_VALUE, machine_id: ALL_VALUE }
				: {}),
			...(key === "user_role" ? { user_email: ALL_VALUE } : {}),
		}));
		if (key === "legal_entity") setLegalEntityInput("");
		if (key === "customer_id") setCustomerInput("");
		if (key === "site_id" || key === "customer_id" || key === "legal_entity") setSiteInput("");
		if (key === "user_role") {
			setRoleInput("");
			setUserInput("");
		}
		if (key === "user_email" || key === "customer_id" || key === "legal_entity") setUserInput("");
		if (key === "machine_id" || key === "customer_id" || key === "legal_entity") setMachineInput("");
	};

	const appliedFilters = useMemo(
		() =>
			FILTER_KEYS
				.filter((filter) => !isAllValue(filters[filter.key]))
				.map((filter) => {
					if (filter.key === "user_role") {
						const roleLabels = (Array.isArray(filters.user_role) ? filters.user_role : [])
							.map((value) =>
								(filterOptionMap.user_role || []).find((option) => String(option.value) === String(value))?.label
								|| value
							)
							.filter(Boolean);
						return {
							...filter,
							value: filters.user_role,
							display: `${filter.label}: ${roleLabels.join(", ")}`,
						};
					}
					const selectedOption = (filterOptionMap[filter.key] || []).find(
						(option) => String(option.value) === String(filters[filter.key])
					);
					return {
						...filter,
						value: filters[filter.key],
						display: `${filter.label}: ${selectedOption?.label || filters[filter.key]}`,
					};
				}),
		[filterOptionMap, filters]
	);
	const hasActiveFilters = appliedFilters.length > 0;
	const compactFilterControlSx = {
		flex: {
			xs: "1 1 100%",
			sm: "1 1 calc(50% - 8px)",
			md: "1 1 calc(33.333% - 10px)",
			lg: "1 1 180px",
			xl: "1 1 200px",
		},
		minWidth: { xs: "100%", sm: "calc(50% - 8px)", md: 180, lg: 168, xl: 180 },
		maxWidth: { xs: "100%", sm: "none", lg: 240, xl: 260 },
		"& .MuiOutlinedInput-root": {
			height: { xs: 36, sm: 38, md: 40 },
			bgcolor: "background.paper",
		},
		"& .MuiInputBase-input": {
			fontSize: { xs: 12, sm: 13, md: 14 },
			py: 0.25,
		},
		"& .MuiInputLabel-root": {
			fontSize: { xs: 12, sm: 13, md: 14 },
			maxWidth: "calc(100% - 28px)",
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		"& .MuiAutocomplete-endAdornment": {
			right: { xs: 4, sm: 9 },
		},
	};
	// The role field's custom text summary sits next to the (empty) search
	// input rather than inside it. Without nowrap, once the summary text gets
	// long the row wraps and the leftover empty input drops to its own line,
	// where its blinking caret shows up as a stray "|". Force nowrap + clip
	// so the empty input stays off-screen instead of wrapping below.
	const roleFilterControlSx = {
		...compactFilterControlSx,
		"& .MuiOutlinedInput-root": {
			...compactFilterControlSx["& .MuiOutlinedInput-root"],
			flexWrap: "nowrap",
			overflow: "hidden",
		},
		"& .MuiAutocomplete-input": {
			minWidth: 0,
		},
	};
	// Show the role selection as plain text (like every other filter field)
	// instead of chips, on every screen size: first role truncated to 5 chars,
	// plus "+N" for any additional roles. Full list available on hover.
	const ROLE_TAG_MAX_CHARS = 5;
	const renderRoleSummaryTags = (selected) => {
		if (!selected || selected.length === 0) return null;
		const label = selected[0]?.label || "";
		const truncated = label.length > ROLE_TAG_MAX_CHARS ? `${label.slice(0, ROLE_TAG_MAX_CHARS)}…` : label;
		const extraCount = selected.length - 1;
		const fullTitle = selected.map((option) => option?.label).filter(Boolean).join(", ");
		return (
			<Box
				component="span"
				title={fullTitle}
				sx={{
					ml: "14px",
					display: "inline-flex",
					alignItems: "center",
					minWidth: 0,
					maxWidth: "100%",
					fontSize: "inherit",
					color: "text.primary",
				}}
			>
				<Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
					{truncated}
				</Box>
				{extraCount > 0 && (
					<Box component="span" sx={{ flexShrink: 0, ml: "3px" }}>
						+{extraCount}
					</Box>
				)}
			</Box>
		);
	};

	const filterInputMap = {
		legal_entity: legalEntityInput,
		customer_id: customerInput,
		site_id: siteInput,
		user_role: roleInput,
		user_email: userInput,
		machine_id: machineInput,
	};

	const debouncedFilterInputMap = {
		legal_entity: debouncedLegalEntityInput,
		customer_id: debouncedCustomerInput,
		site_id: debouncedSiteInput,
		user_role: debouncedRoleInput,
		user_email: debouncedUserInput,
		machine_id: debouncedMachineInput,
	};

	const handleFilterInputChange = (key, value) => {
		if (key === "legal_entity") setLegalEntityInput(value);
		if (key === "customer_id") setCustomerInput(value);
		if (key === "site_id") setSiteInput(value);
		if (key === "user_role") setRoleInput(value);
		if (key === "user_email") setUserInput(value);
		if (key === "machine_id") setMachineInput(value);
	};

	const getDisplayedFilterOptions = (key, options, selectedOption) => {
		const query = getSearchQuery(debouncedFilterInputMap[key], selectedOption);
		const filtered = query
			? options.filter((option) => (option.searchText || option.label || "").toLowerCase().includes(query))
			: options;
		return filtered.slice(0, OPTION_LIMIT);
	};

	const renderContentLoader = (minHeight = 220) => (
		<Box sx={{ minHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
			<ChartLoading />
		</Box>
	);
	const renderFunnelChart = (title, option) => (
		<Paper variant="outlined" sx={{ p: 2.5, mt: 3 }}>
			<SectionTitle>{title}</SectionTitle>
			{isRefreshingDashboard ? (
				renderContentLoader(420)
			) : option?.hasData ? (
				<Box sx={{ height: { xs: 360, md: 420 } }}>
					<ReactECharts
						option={option}
						style={{ height: "100%", width: "100%" }}
						notMerge
						lazyUpdate
					/>
				</Box>
			) : (
				<Typography color="text.secondary">
					No funnel data for the selected filters.
				</Typography>
			)}
		</Paper>
	);

	if (!canSeeAnalytics) {
		return <NotAuthorized />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: "100vw",
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				py: 3,
				px: { xs: 2, md: 5 },
				overflowX: "hidden",
			}}
		>
			<PageTitle title="User analytics" />

			<Paper
				elevation={0}
				sx={{
					px: { xs: 0, md: 0 },
					py: { xs: 1, md: 1.5 },
					mb: 2,
					border: 0,
					borderRadius: 0,
					background: "inherit",
					boxShadow: "none",
					position: { xs: "static", md: "sticky" },
					top: { md: 75 },
					zIndex: 1051,
				}}
			>
				<Stack
					direction={{ xs: "column", xl: "row" }}
					alignItems={{ xs: "stretch", xl: "center" }}
					justifyContent="space-between"
					spacing={{ xs: 1.5, xl: 1.5 }}
					useFlexGap
					flexWrap="nowrap"
				>
					<Stack
						direction="row"
						alignItems="stretch"
						spacing={{ xs: 0.75, sm: 1 }}
						useFlexGap
						flexWrap="wrap"
						sx={{
							flex: { xs: "1 1 100%", xl: "1 1 auto" },
							minWidth: 0,
							width: "100%",
						}}
					>
						{FILTER_KEYS.map((filter) => {
							const options = filterOptionMap[filter.key] || [];
							const isMultiRole = filter.key === "user_role";
							const selectedOption = isMultiRole
								? null
								: options.find((option) => String(option.value) === String(filters[filter.key])) || null;
							const selectedRoleOptions = isMultiRole
								? (Array.isArray(filters.user_role) && filters.user_role.length > 0
									? options.filter((option) =>
										filters.user_role.map(String).includes(String(option.value))
									)
									: options.filter((option) => option.value === ALL_VALUE))
								: [];
							const displayedOptions = getDisplayedFilterOptions(filter.key, options, selectedOption);

							return (
								<Autocomplete
									key={filter.key}
									multiple={isMultiRole}
									disableCloseOnSelect={isMultiRole}
									{...(isMultiRole ? { renderTags: renderRoleSummaryTags } : {})}
									size="small"
									disablePortal
									disableClearable={false}
									options={displayedOptions}
									value={isMultiRole ? selectedRoleOptions : selectedOption}
									inputValue={filterInputMap[filter.key]}
									filterOptions={(items) => items}
									loading={filterLoading}
									getOptionLabel={(option) => option?.label || ""}
									isOptionEqualToValue={(option, value) =>
										String(option?.value) === String(value?.value)
									}
									renderOption={(props, option) =>
										filter.key === "user_email" ? (
											<li {...props}>
												<Tooltip title={option?.label || ""} placement="right">
													<Box sx={{ minWidth: 0 }}>
														<Typography
															variant="body1"
															noWrap
															sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
														>
															{option?.displayName || option?.label || ""}
														</Typography>
														{option?.email ? (
															<Typography
																variant="body2"
																color="text.secondary"
																noWrap
																sx={{
																	fontSize: "11px",
																	maxWidth: "100%",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{option.email}
															</Typography>
														) : null}
													</Box>
												</Tooltip>
											</li>
										) : (
											<li {...props}>{option?.label || ""}</li>
										)
									}
									onInputChange={(_, value) => handleFilterInputChange(filter.key, value)}
									onChange={(_, optionOrOptions) => {
										if (isMultiRole) {
											const nextValues = (Array.isArray(optionOrOptions) ? optionOrOptions : [])
												.map((opt) => opt?.value)
												.filter((value) => value !== undefined && value !== null && value !== ALL_VALUE);
											handleFilterChange(filter.key, nextValues);
											return;
										}
										handleFilterChange(filter.key, optionOrOptions?.value ?? ALL_VALUE);
									}}
									slotProps={{
										popper: {
											placement: "bottom-start",
											modifiers: [{ name: "flip", enabled: false }],
										},
										paper: {
											sx: { mt: 0.5 },
										},
										listbox: {
											sx: { maxHeight: 220, overflow: "auto" },
										},
									}}
									sx={isMultiRole ? roleFilterControlSx : compactFilterControlSx}
									renderInput={(params) => (
										<TextField
											{...params}
											size="small"
											label={`Filter by ${filter.label}`}
											placeholder={isMultiRole && selectedRoleOptions.length > 0 ? "" : filter.allLabel}
											variant="outlined"
										/>
									)}
								/>
							);
						})}
					</Stack>

					<Stack
						direction={{ xs: "column", sm: "row" }}
						alignItems="center"
						justifyContent={{ xs: "flex-start", xl: "flex-end" }}
						spacing={{ xs: 0.9, xl: 0.9 }}
						useFlexGap
						flexWrap={{ xs: "wrap", xl: "nowrap" }}
						sx={{ flex: "0 0 auto", width: { xs: "100%", xl: "auto" }, ml: { xl: "auto" } }}
					>
						<Stack
							direction="row"
							alignItems="center"
							spacing={{ xs: 0.5, sm: 0.75 }}
							useFlexGap
							flexWrap={{ xs: "wrap", xl: "nowrap" }}
						>
							{DATE_RANGE_BUTTONS.map((button) => {
								const selected = selectedRange.key === button.key;
								return (
									<Tooltip key={button.key} title={button.tooltip} arrow>
										<Button
											type="button"
											aria-label={button.label}
											variant="roundedGreyBgSquareContained"
											onClick={() => handleDateRangeClick(button.key)}
											sx={{
												width: { xs: 28, sm: 35 },
												height: { xs: 28, sm: 35 },
												minWidth: { xs: 28, sm: 35 },
												minHeight: { xs: 28, sm: 35 },
												fontSize: { xs: 12, sm: 14 },
												background: selected
													? (theme) => theme.palette.primary.main
													: (theme) => alpha(theme.palette.primary.light, 0.2),
												color: selected ? "#fff" : (theme) => theme.palette.primary.main,
											}}
										>
											{button.key}
										</Button>
									</Tooltip>
								);
							})}
						</Stack>

						<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

						<Box sx={{ width: { xs: "100%", sm: 300, xl: 300 } }}>
							<DateRange
								setSelectedRange={() => { }}
								selectedRange={selectedRange}
								selectedDateRange={selectedDateRange}
								setSelectedDateRange={handleDateRangePickerChange}
							/>
						</Box>
					</Stack>
				</Stack>

				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "flex-start", md: "center" }}
					justifyContent="space-between"
					spacing={2}
					sx={{ mt: 2.5 }}
				>
					<Box sx={{ minWidth: 180 }}>
						<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
							{selectedRange.label}
						</Typography>
						<Typography variant="body1" sx={{ color: "#111827" }}>
							{formatDisplayDate(selectedRange.startDate)} - {formatDisplayDate(selectedRange.endDate)}
						</Typography>
					</Box>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent={{ xs: "flex-start", md: "flex-end" }}
						spacing={1}
						useFlexGap
						flexWrap="wrap"
						sx={{ minHeight: 32, flex: "1 1 auto" }}
					>
						{appliedFilters.length > 0 ? (
							<>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<FilterAltIcon color="primary" fontSize="small" />
									<Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
										Applied Filters :
									</Typography>
								</Stack>
								{appliedFilters.map((filter) => (
									<Chip
										key={filter.key}
										size="small"
										label={filter.display}
										onDelete={() => handleClearFilter(filter.key)}
										sx={{
											height: 32,
											borderRadius: 1,
											bgcolor: "#eeeeee",
											color: "#111827",
											fontWeight: 600,
											"& .MuiChip-label": {
												maxWidth: { xs: 220, md: 420 },
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
									/>
								))}
								<Button
									type="button"
									variant="text"
									size="small"
									onClick={handleReset}
									startIcon={<FilterAltOffIcon />}
									sx={{
										fontWeight: 800,
										textTransform: "none",
										ml: { xs: 0, md: 1 },
									}}
								>
									Clear All Filters
								</Button>
							</>
						) : null}
					</Stack>
				</Stack>
				<Box sx={{ mt: 1.5, height: 4 }}>{loading ? <LinearProgress /> : null}</Box>
			</Paper>

			{error ? (
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: "error.main" }}>
					<Typography color="error">{error}</Typography>
				</Paper>
			) : null}

			{filterError ? (
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: "warning.main" }}>
					<Typography color="warning.main">{filterError}</Typography>
				</Paper>
			) : null}

			<Box>
				<Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 2.5 },
						mb: 3,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						boxShadow: "none",
						backgroundColor: "background.paper",
					}}
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						alignItems={{ xs: "flex-start", sm: "center" }}
						justifyContent="space-between"
						spacing={1.5}
						sx={{ mb: 2 }}
					>
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
								Core engagement
							</Typography>
						</Box>
					</Stack>

					<Grid container spacing={2} alignItems="stretch">
						{featuredSummaryCards.map((card) => (
							<Grid key={card.label} item xs={12} sm={4}>
								<MetricCard {...card} loading={isRefreshingDashboard} />
							</Grid>
						))}
					</Grid>

				</Paper>

				<Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 2.5 },
						mb: 3,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						boxShadow: "none",
						backgroundColor: "background.paper",
					}}
				>
					<SectionTitle>Engagement depth</SectionTitle>
					<Grid container spacing={1.5} alignItems="stretch">
						{activitySummaryCards.map((card) => (
							<Grid key={card.label} item xs={12} sm={6} md={4}>
								<MetricCard {...card} compact loading={isRefreshingDashboard} />
							</Grid>
						))}
					</Grid>
				</Paper>

				<Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 2.5 },
						mb: 3,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						boxShadow: "none",
						backgroundColor: "background.paper",
					}}
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						alignItems={{ xs: "flex-start", sm: "center" }}
						justifyContent="space-between"
						spacing={1.5}
						sx={{ mb: 2 }}
					>
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
								Content engagement
							</Typography>
						</Box>
					</Stack>

					<Grid container spacing={2}>
						{documentSummaryCards.map((card) => (
							<Grid key={card.label} item xs={12} sm={6} md={4}>
								<MetricCard {...card} loading={isRefreshingDashboard} />
							</Grid>
						))}
					</Grid>
				</Paper>
				<Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 2.5 },
						mb: 3,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						boxShadow: "none",
						backgroundColor: "background.paper",
					}}
				>
					<SectionTitle>Marketing and promotions</SectionTitle>
					<Grid container spacing={2}>
						{marketingSummaryCards.map((card) => (
							<Grid key={card.label} item xs={12} sm={6}>
								<MetricCard {...card} loading={isRefreshingDashboard} />
							</Grid>
						))}
					</Grid>
				</Paper>
				{/* <Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 2.5 },
						mb: 3,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						boxShadow: "none",
						backgroundColor: "background.paper",
					}}
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						alignItems={{ xs: "flex-start", sm: "center" }}
						justifyContent="space-between"
						spacing={1.5}
						sx={{ mb: 2 }}
					>
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
								Content Aggregation
							</Typography>
						</Box>
					</Stack>

					<Grid container spacing={2}>
						{contentAggregationCards.map((card) => (
							<Grid key={card.label} item xs={12} sm={6} md={4} lg={2.4}>
								<MetricCard {...card} loading={isRefreshingDashboard} />
							</Grid>
						))}
					</Grid>
				</Paper> */}
				<Grid container spacing={3}>
					<Grid item xs={12} lg={5}>
						<Paper variant="outlined" sx={{ p: 2.5 }}>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								alignItems={{ xs: "flex-start", sm: "center" }}
								justifyContent="space-between"
								spacing={1}
								sx={{ mb: 1 }}
							>
								<Typography variant="h6" sx={{ fontWeight: 700 }}>
									Top Pages by Traffic
								</Typography>
								<Chip
									size="small"
									label={
										isPageTrafficRefreshing ? (
											<Skeleton
												variant="text"
												animation="wave"
												width={58}
												height={18}
												sx={{ display: "block" }}
											/>
										) : (
											<ExactNumber value={pageTrafficRowCount} suffix=" pages" />
										)
									}
									sx={{ fontWeight: 700, borderRadius: 1 }}
								/>
							</Stack>
							{isPageTrafficRefreshing ? (
								renderContentLoader(USER_ANALYTICS_TABLE_GRID_HEIGHT)
							) : (
								<Datagrid
									data={pageTrafficRows}
									columns={pageTrafficColumns}
									rowIdField="page_traffic_key"
									showSearchBar
									pageSize={pageTrafficPaginationModel.pageSize}
									paginationMode="server"
									rowCount={pageTrafficRowCount}
									paginationModel={pageTrafficPaginationModel}
									onPaginationModelChange={handlePageTrafficPaginationModelChange}
									autoHeight={false}
									gridHeight={USER_ANALYTICS_TABLE_GRID_HEIGHT}
									loading={false}
									slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
									gridSx={USER_ANALYTICS_GRID_SX}
								/>
							)}
						</Paper>
					</Grid>

					<Grid item xs={12} lg={7}>
						<Paper variant="outlined" sx={{ p: 2.5 }}>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								alignItems={{ xs: "flex-start", sm: "center" }}
								justifyContent="space-between"
								spacing={1}
								sx={{ mb: 1 }}
							>
								<Typography variant="h6" sx={{ fontWeight: 700 }}>
									User Activity
								</Typography>
								<Chip
									size="small"
									label={
										isUserActivityRefreshing ? (
											<Skeleton
												variant="text"
												animation="wave"
												width={58}
												height={18}
												sx={{ display: "block" }}
											/>
										) : (
											<ExactNumber value={userAnalyticsRowCount} suffix=" users" />
										)
									}
									sx={{ fontWeight: 700, borderRadius: 1 }}
								/>
							</Stack>
							{isUserActivityRefreshing ? (
								renderContentLoader(USER_ANALYTICS_TABLE_GRID_HEIGHT)
							) : (
								<Datagrid
									data={userAnalyticsRows}
									columns={userAnalyticsColumns}
									rowIdField="analytics_user_key"
									showSearchBar
									pageSize={userPaginationModel.pageSize}
									paginationMode="server"
									rowCount={userAnalyticsRowCount}
									paginationModel={userPaginationModel}
									onPaginationModelChange={handleUserPaginationModelChange}
									autoHeight={false}
									gridHeight={USER_ANALYTICS_TABLE_GRID_HEIGHT}
									loading={false}
									slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
									gridSx={USER_ANALYTICS_GRID_SX}
								/>
							)}
						</Paper>
					</Grid>
				</Grid>
				<Grid container spacing={3} sx={{ mt: 0 }}>
					<Grid item xs={12}>
						<Paper variant="outlined" sx={{ p: 2.5 }}>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								alignItems={{ xs: "flex-start", sm: "center" }}
								justifyContent="space-between"
								spacing={1}
								sx={{ mb: 1 }}
							>
								<Typography variant="h6" sx={{ fontWeight: 700 }}>
									Top Searches
								</Typography>
								<Chip
									size="small"
									label={
										isSearchAnalyticsRefreshing ? (
											<Skeleton
												variant="text"
												animation="wave"
												width={58}
												height={18}
												sx={{ display: "block" }}
											/>
										) : (
											<ExactNumber value={searchAnalyticsRowCount} suffix=" terms" />
										)
									}
									sx={{ fontWeight: 700, borderRadius: 1 }}
								/>
							</Stack>
							{isSearchAnalyticsRefreshing ? (
								renderContentLoader(USER_ANALYTICS_TABLE_GRID_HEIGHT)
							) : (
								<Datagrid
									data={searchAnalyticsRows}
									columns={searchAnalyticsColumns}
									rowIdField="search_analytics_key"
									showSearchBar
									pageSize={searchAnalyticsPaginationModel.pageSize}
									paginationMode="server"
									rowCount={searchAnalyticsRowCount}
									paginationModel={searchAnalyticsPaginationModel}
									onPaginationModelChange={handleSearchAnalyticsPaginationModelChange}
									autoHeight={false}
									gridHeight={USER_ANALYTICS_TABLE_GRID_HEIGHT}
									loading={false}
									slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
									gridSx={USER_ANALYTICS_GRID_SX}
								/>
							)}
						</Paper>
					</Grid>
				</Grid>
				<Grid container spacing={3} sx={{ mt: 0 }}>
					<Grid item xs={12} lg={6}>
						<Paper variant="outlined" sx={{ p: 2.5 }}>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								alignItems={{ xs: "flex-start", sm: "center" }}
								justifyContent="space-between"
								spacing={1}
								sx={{ mb: 1 }}
							>
								<Typography variant="h6" sx={{ fontWeight: 700 }}>
									Banner Engagement
								</Typography>
								<Chip
									size="small"
									label={
										isBannerEngagementRefreshing ? (
											<Skeleton
												variant="text"
												animation="wave"
												width={58}
												height={18}
												sx={{ display: "block" }}
											/>
										) : (
											<ExactNumber value={bannerEngagementRowCount} suffix=" records" />
										)
									}
									sx={{ fontWeight: 700, borderRadius: 1 }}
								/>
							</Stack>
							{isBannerEngagementRefreshing ? (
								renderContentLoader(USER_ANALYTICS_TABLE_GRID_HEIGHT)
							) : (
								<Datagrid
									data={bannerEngagementRows}
									columns={bannerEngagementColumns}
									rowIdField="banner_engagement_key"
									showSearchBar
									pageSize={bannerEngagementPaginationModel.pageSize}
									paginationMode="server"
									rowCount={bannerEngagementRowCount}
									paginationModel={bannerEngagementPaginationModel}
									onPaginationModelChange={handleBannerEngagementPaginationModelChange}
									autoHeight={false}
									gridHeight={USER_ANALYTICS_TABLE_GRID_HEIGHT}
									loading={false}
									slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
									gridSx={USER_ANALYTICS_GRID_SX}
								/>
							)}
						</Paper>
					</Grid>

					<Grid item xs={12} lg={6}>
						<Paper variant="outlined" sx={{ p: 2.5 }}>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								alignItems={{ xs: "flex-start", sm: "center" }}
								justifyContent="space-between"
								spacing={1}
								sx={{ mb: 1 }}
							>
								<Typography variant="h6" sx={{ fontWeight: 700 }}>
									Promo Code Usage
								</Typography>
								<Chip
									size="small"
									label={
										isPromotionUsageRefreshing ? (
											<Skeleton
												variant="text"
												animation="wave"
												width={58}
												height={18}
												sx={{ display: "block" }}
											/>
										) : (
											<ExactNumber value={promotionUsageRowCount} suffix=" records" />
										)
									}
									sx={{ fontWeight: 700, borderRadius: 1 }}
								/>
							</Stack>
							{isPromotionUsageRefreshing ? (
								renderContentLoader(USER_ANALYTICS_TABLE_GRID_HEIGHT)
							) : (
								<Datagrid
									data={promotionUsageRows}
									columns={promotionUsageColumns}
									rowIdField="promotion_usage_key"
									showSearchBar
									pageSize={promotionUsagePaginationModel.pageSize}
									paginationMode="server"
									rowCount={promotionUsageRowCount}
									paginationModel={promotionUsagePaginationModel}
									onPaginationModelChange={handlePromotionUsagePaginationModelChange}
									autoHeight={false}
									gridHeight={USER_ANALYTICS_TABLE_GRID_HEIGHT}
									loading={false}
									slotProps={USER_ANALYTICS_GRID_SLOT_PROPS}
									gridSx={USER_ANALYTICS_GRID_SX}
								/>
							)}
						</Paper>
					</Grid>
				</Grid>
				{renderFunnelChart("Order Conversion Funnel", orderFunnelChartOption)}
				{/* {renderFunnelChart("2D Model Flow", twoDModelChartOption)} */}
				{/* {renderFunnelChart("3D Model Flow", threeDModelChartOption)} */}
			</Box>
		</Box>
	);
};

export default UserAnalytics;
